/**
 * Create Sandbox page component.
 *
 * @module pages/CreateSandboxPage
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams, useParams } from 'react-router-dom';
import { ArrowLeft, Save, ChevronDown, X, CheckCircle, Circle, Plus, Info } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Input,
  Select,
  Checkbox,
  LabelsEditor,
  PortsEditor,
  EgressPolicyEditor,
  sanitizeEgressHeaders,
  WaterfallChart,
  ContentPackageDownloadsEditor,
  TelemetryDataPicker,
} from '@/components/ui';
import type { LabelsEditorRef, PortEntry, ContentPackageDownloadEntry } from '@/components/ui';
import {
  useCreateSandbox,
  useDiskImages,
  useAllSnapshots,
  usePublicDiskImages,
  usePreviewMode,
  useSandboxGroup,
  useSandboxGroupDiskImages,
  useSandboxGroupSecrets,
  useSandboxGroupSnapshots,
  useSandboxGroupVolumes,
  useAgentIdentities,
  useVolumes,
  useSecrets,
  useContentPackages,
} from '@/hooks';
import { connectionService } from '@/services/connectionService';
import type {
  SandboxRequest,
  SandboxResponse,
  ConnectionResponse,
  EgressHostRule,
  EgressPolicyAction,
  EgressPolicyRule,
  SandboxSuspendMode,
  TelemetryData,
  TelemetryProtocol,
  TelemetryAuth,
  TelemetrySecretRef,
  TrafficInspection,
} from '@/types';
import { ApiException } from '@/services/httpClient';
import { useAuth } from '@/contexts/AuthContext';
import { parseDuration, formatDurationLong } from '@/utils';

type SourceType = 'diskImage' | 'publicDiskImage' | 'snapshot' | 'pod';
type VmmType = 'CloudHypervisor' | 'Firecracker';

interface VolumeMountEntry {
  volumeName: string;
  mountpoint: string;
  readOnly: boolean;
}

/** Pod-level volume declaration form entry */
interface PodVolumeEntry {
  name: string;
  kind: 'AzureBlob' | 'Local' | 'DataDisk';
  readOnly: boolean; // only for AzureBlob
  fileCacheSizeLimit: string; // only for AzureBlob, e.g. "256Mi"
  size: string; // only for Local, e.g. "256Mi"
}

/** Pod-level content package declaration form entry */
interface PodContentPackageEntry {
  contentPackageId: string;
}

/** Per-container form state for pod source type */
interface PodContainerFormState {
  name: string;
  diskImageType: 'public' | 'private';
  diskImageId: string;
  publicDiskImageId: string;
  command: string;
  args: string;
  environment: Record<string, string>;
  volumeMounts: VolumeMountEntry[];
  contentPackageDownloads: ContentPackageDownloadEntry[];
  cpuLimit: string;
  memoryLimit: string;
}

/** Draft OTLP endpoint entry for UI form */
interface OtlpEndpointEntry {
  endpoint: string;
  protocol: TelemetryProtocol;
  data: TelemetryData[];
  /** Optional auth configuration */
  auth?: TelemetryAuth;
}

/** Draft Log Analytics Legacy endpoint entry for UI form */
interface LogAnalyticsLegacyEndpointEntry {
  workspaceId: string;
  tableName: string;
  data: TelemetryData[];
  auth: TelemetrySecretRef;
}

/** Draft Log Analytics (MI) endpoint entry for UI form */
interface LogAnalyticsEndpointEntry {
  dceEndpoint: string;
  dcrImmutableId: string;
  tableName: string;
  data: TelemetryData[];
}

type TelemetryEndpointKind = 'OTLP' | 'LogAnalytics' | 'LogAnalyticsLegacy';

interface FormState {
  sourceType: SourceType;
  vmmType: VmmType;
  labels: Record<string, string>;
  environment: Record<string, string>;
  diskImageId: string;
  publicDiskImageId: string;
  snapshotId: string;
  cpu: string;
  memory: string;
  disk: string;
  entrypoint: string;
  cmd: string;
  selectedConnections: string[];
  ports: PortEntry[];
  egressDefaultAction: EgressPolicyAction;
  egressHostRules: EgressHostRule[];
  egressRules: EgressPolicyRule[];
  trafficInspection: TrafficInspection;
  sandboxGroupId: string;
  autoSuspendEnabled: boolean;
  autoSuspendInterval: string;
  autoSuspendMode: SandboxSuspendMode;
  autoDeleteEnabled: boolean;
  autoDeleteIntervalSeconds: string;
  skipEgressProxy: boolean;
  agentIdentityId: string;
  volumeMounts: VolumeMountEntry[];
  otlpEndpoints: OtlpEndpointEntry[];
  logAnalyticsEndpoints: LogAnalyticsEndpointEntry[];
  logAnalyticsLegacyEndpoints: LogAnalyticsLegacyEndpointEntry[];
  contentPackageDownloads: ContentPackageDownloadEntry[];
  /** Pod-level volume declarations — only used when sourceType === 'pod' */
  podVolumes: PodVolumeEntry[];
  /** Pod-level content package declarations — only used when sourceType === 'pod' */
  podContentPackages: PodContentPackageEntry[];
  /** Pod containers — only used when sourceType === 'pod' */
  podContainers: PodContainerFormState[];
}

const createEmptyPodContainer = (): PodContainerFormState => ({
  name: '',
  diskImageType: 'public',
  diskImageId: '',
  publicDiskImageId: '',
  command: '',
  args: '',
  environment: {},
  volumeMounts: [],
  contentPackageDownloads: [],
  cpuLimit: '',
  memoryLimit: '',
});

const initialFormState: FormState = {
  sourceType: 'publicDiskImage',
  vmmType: 'CloudHypervisor',
  labels: {},
  environment: {},
  diskImageId: '',
  publicDiskImageId: '',
  snapshotId: '',
  cpu: '1000m',
  memory: '2Gi',
  disk: '20Gi',
  entrypoint: '',
  cmd: '',
  selectedConnections: [],
  ports: [],
  egressDefaultAction: 'Allow',
  egressHostRules: [],
  egressRules: [],
  trafficInspection: 'Legacy' as TrafficInspection,
  sandboxGroupId: '',
  autoSuspendEnabled: true,
  autoSuspendInterval: '5m',
  autoSuspendMode: 'Memory',
  autoDeleteEnabled: false,
  autoDeleteIntervalSeconds: '1d',
  skipEgressProxy: false,
  agentIdentityId: '',
  volumeMounts: [],
  otlpEndpoints: [],
  logAnalyticsEndpoints: [],
  logAnalyticsLegacyEndpoints: [],
  contentPackageDownloads: [],
  podVolumes: [],
  podContentPackages: [],
  podContainers: [createEmptyPodContainer()],
};

/**
 * Create sandbox page
 */
export function CreateSandboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { groupId: routeGroupId } = useParams<{ groupId?: string }>();
  const { user } = useAuth();
  const createMutation = useCreateSandbox();
  const { data: privateDiskImages, isLoading: privateDiskImagesLoading } = useDiskImages();
  const { data: publicDiskImages, isLoading: publicDiskImagesLoading } = usePublicDiskImages();
  const { data: snapshotsData, isLoading: snapshotsLoading } = useAllSnapshots();
  const { data: agentIdentities, isLoading: agentIdentitiesLoading } = useAgentIdentities(1, 100);
  const { data: userVolumesData, isLoading: userVolumesLoading } = useVolumes(
    1,
    100,
    !routeGroupId
  );
  const { data: groupVolumesData, isLoading: groupVolumesLoading } = useSandboxGroupVolumes(
    routeGroupId ?? '',
    !!routeGroupId
  );
  const volumesData = routeGroupId ? groupVolumesData : userVolumesData;
  const volumesLoading = routeGroupId ? groupVolumesLoading : userVolumesLoading;
  const { data: userSecretsData } = useSecrets(!routeGroupId);
  const { data: groupSecretsData } = useSandboxGroupSecrets(routeGroupId ?? '', !!routeGroupId);
  const secretsData = routeGroupId ? groupSecretsData : userSecretsData;
  const { data: contentPackagesData, isLoading: contentPackagesLoading } = useContentPackages();

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);
  // Ref for environment editor to get uncommitted draft
  const environmentEditorRef = useRef<LabelsEditorRef>(null);

  // Connections state
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const isPreviewMode = usePreviewMode();
  const showFirecracker = searchParams.get('show_firecracker') === 'true';

  // Get diskImageId, publicDiskImageId, snapshotId, or groupId from URL params if provided
  const preselectedDiskImageId = searchParams.get('diskImageId') || '';
  const preselectedPublicDiskImageId = searchParams.get('publicDiskImageId') || '';
  const preselectedSnapshotId = searchParams.get('snapshotId') || '';
  const preselectedGroupId = routeGroupId || searchParams.get('groupId') || '';
  const preselectedProjectId = searchParams.get('projectId') || '';

  const [form, setForm] = useState<FormState>(() => ({
    ...initialFormState,
    sourceType: preselectedSnapshotId
      ? 'snapshot'
      : preselectedDiskImageId
        ? 'diskImage'
        : preselectedPublicDiskImageId
          ? 'publicDiskImage'
          : 'publicDiskImage',
    diskImageId: preselectedDiskImageId,
    publicDiskImageId: preselectedPublicDiskImageId,
    snapshotId: preselectedSnapshotId,
    sandboxGroupId: preselectedGroupId,
    // Auto-fill clone command when coming from a project
    cmd: preselectedProjectId ? '["/bin/sh", "-c", "git clone $GIT_CLONE_URL /workspace"]' : '',
  }));

  // Connector type names that act on behalf of the user's identity
  const PERSONAL_CONNECTOR_TYPES = useMemo(() => new Set(['office365-adc', 'm365copilot-adc']), []);

  // Check if any selected connection uses a personal connector
  const hasPersonalConnectors = useMemo(() => {
    return form.selectedConnections.some((connId) => {
      const conn = connections.find((c) => c.id === connId);
      return conn ? PERSONAL_CONNECTOR_TYPES.has(conn.type.toLowerCase()) : false;
    });
  }, [form.selectedConnections, connections, PERSONAL_CONNECTOR_TYPES]);

  // Fetch the selected group to get its connections (for inheriting)
  const { data: selectedGroup } = useSandboxGroup(form.sandboxGroupId);

  // Group-scoped disk images and snapshots (only fetched when a group is selected)
  const { data: groupDiskImages, isLoading: groupDiskImagesLoading } = useSandboxGroupDiskImages(
    form.sandboxGroupId || '',
    !!form.sandboxGroupId
  );
  const { data: groupSnapshots } = useSandboxGroupSnapshots(
    form.sandboxGroupId || '',
    !!form.sandboxGroupId
  );

  const diskImagesLoading = form.sandboxGroupId
    ? groupDiskImagesLoading
    : privateDiskImagesLoading || publicDiskImagesLoading;

  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [showResults, setShowResults] = useState(false);
  const [additionalDetailsExpanded, setAdditionalDetailsExpanded] = useState(false);
  const [portsExpanded, setPortsExpanded] = useState(false);
  const [lifecyclePolicyExpanded, setLifecyclePolicyExpanded] = useState(false);
  const [egressPolicyExpanded, setEgressPolicyExpanded] = useState(false);
  const [volumesExpanded, setVolumesExpanded] = useState(false);
  const [telemetryExpanded, setTelemetryExpanded] = useState(false);
  const [draftOtlpEndpoint, setDraftOtlpEndpoint] = useState<OtlpEndpointEntry>({
    endpoint: '',
    protocol: 'Http',
    data: [],
    auth: undefined,
  });
  const [draftEndpointKind, setDraftEndpointKind] = useState<TelemetryEndpointKind>('OTLP');
  const [draftLogAnalyticsEndpoint, setDraftLogAnalyticsEndpoint] =
    useState<LogAnalyticsEndpointEntry>({
      dceEndpoint: '',
      dcrImmutableId: '',
      tableName: '',
      data: [],
    });
  const [draftLegacyEndpoint, setDraftLegacyEndpoint] = useState<LogAnalyticsLegacyEndpointEntry>({
    workspaceId: '',
    tableName: '',
    data: ['ContainerStdoutStderr'],
    auth: { secretId: '', secretKey: '' },
  });
  const [draftVolumeMount, setDraftVolumeMount] = useState<VolumeMountEntry>({
    volumeName: '',
    mountpoint: '',
    readOnly: false,
  });
  const [draftVolumeMountKind, setDraftVolumeMountKind] = useState<'AzureBlob' | 'DataDisk'>(
    'AzureBlob'
  );
  const [mountPathError, setMountPathError] = useState<string | undefined>();
  const [contentPackagesExpanded, setContentPackagesExpanded] = useState(false);
  const [podContainerTabs, setPodContainerTabs] = useState<Record<number, string>>({});
  // Which container is currently expanded for editing (-1 = none, but new containers auto-expand)
  const [editingContainerIdx, setEditingContainerIdx] = useState<number>(0);
  // Per-container volume mount draft state (keyed by container index)
  const [podVolumeDrafts, setPodVolumeDrafts] = useState<
    Record<number, VolumeMountEntry & { visible: boolean }>
  >({});
  const [podMountPathErrors, setPodMountPathErrors] = useState<Record<number, string | undefined>>(
    {}
  );
  // Pod-level volume/content package section state
  const [podVolumeSectionExpanded, setPodVolumeSectionExpanded] = useState(false);
  const [podContentPackageSectionExpanded, setPodContentPackageSectionExpanded] = useState(false);
  const [podVolumeDraftName, setPodVolumeDraftName] = useState('');
  const [podVolumeDraftReadOnly, setPodVolumeDraftReadOnly] = useState(false);
  const [podVolumeDraftKind, setPodVolumeDraftKind] = useState<'AzureBlob' | 'Local' | 'DataDisk'>(
    'AzureBlob'
  );
  const [podVolumeDraftSize, setPodVolumeDraftSize] = useState('256Mi');
  const [podVolumeDraftCacheSize, setPodVolumeDraftCacheSize] = useState('256Mi');
  const [podContentPackageDraftId, setPodContentPackageDraftId] = useState('');
  const [createdSandbox, setCreatedSandbox] = useState<SandboxResponse | null>(null);
  const [timeTakenMs, setTimeTakenMs] = useState<number>(0);
  const [creationError, setCreationError] = useState<{
    message: string;
    detail?: string;
    traceId?: string;
    requestId?: string;
    status?: number;
  } | null>(null);

  // Computed: whether any DataDisk volume is selected (non-pod or pod)
  const hasNonPodDataDiskVolumes = form.volumeMounts.some((m) => {
    const vol = volumesData?.find((v) => v.volumeName === m.volumeName);
    return vol?.type === 'DataDisk';
  });
  const hasPodDataDiskVolumes = form.podVolumes.some((v) => v.kind === 'DataDisk');

  // Update diskImageId/snapshotId if it changes in URL (e.g., navigation)
  useEffect(() => {
    if (preselectedDiskImageId && preselectedDiskImageId !== form.diskImageId) {
      setForm((prev) => ({
        ...prev,
        sourceType: 'diskImage',
        diskImageId: preselectedDiskImageId,
      }));
    }
    if (preselectedSnapshotId && preselectedSnapshotId !== form.snapshotId) {
      setForm((prev) => ({ ...prev, sourceType: 'snapshot', snapshotId: preselectedSnapshotId }));
    }
  }, [form.diskImageId, form.snapshotId, preselectedDiskImageId, preselectedSnapshotId]);

  // Fetch available connections (only Ready ones)
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const allConnections = await connectionService.list();
        // Only show Ready connections
        setConnections(allConnections.filter((c) => c.state === 'Ready'));
      } catch (err) {
        console.error('Failed to load connections:', err);
      } finally {
        setConnectionsLoading(false);
      }
    };
    fetchConnections();
  }, []);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'sandboxGroupId' && !value) {
        // Clearing the group — reset group-specific selections
        next.diskImageId = '';
        next.snapshotId = '';
      }
      if (field === 'sourceType') {
        // Switching to pod: clear non-pod volume mounts so they don't leak into the request
        if (value === 'pod') {
          next.volumeMounts = [];
        } else {
          // Switching away from pod: clear pod-specific state
          next.podVolumes = [];
          next.podContainers = [createEmptyPodContainer()];
          next.podContentPackages = [];
        }
      }
      return next;
    });
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {};

    if (form.sourceType === 'diskImage' && !form.diskImageId) {
      newErrors.diskImageId = 'Please select a disk image';
    }

    if (form.sourceType === 'publicDiskImage' && !form.publicDiskImageId) {
      newErrors.publicDiskImageId = 'Please select a public disk image';
    }

    if (form.sourceType === 'snapshot' && !form.snapshotId) {
      newErrors.snapshotId = 'Please select a snapshot';
    }

    // Pod validation
    if (form.sourceType === 'pod') {
      const containerNames = form.podContainers.map((c) => c.name.trim().toLowerCase());
      const hasDuplicateNames = new Set(containerNames).size !== containerNames.length;
      if (hasDuplicateNames) {
        // Use a generic error — we'll show a separate alert for this
        alert('Container names must be unique.');
        return false;
      }
      for (const container of form.podContainers) {
        if (!container.name.trim()) {
          alert('Each container must have a name.');
          return false;
        }
        if (
          !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(container.name.trim()) ||
          container.name.trim().length > 63
        ) {
          alert(
            `Container name "${container.name}" must be DNS label format (lowercase alphanumeric or hyphens, max 63 chars).`
          );
          return false;
        }
        const hasDiskImage =
          container.diskImageType === 'public'
            ? !!container.publicDiskImageId
            : !!container.diskImageId;
        if (!hasDiskImage) {
          alert(
            `Container "${container.name || `#${form.podContainers.indexOf(container) + 1}`}" must have a disk image selected.`
          );
          return false;
        }
      }
    }

    // Only validate CPU/memory when not creating from a snapshot
    if (form.sourceType !== 'snapshot') {
      if (!form.cpu.trim()) {
        newErrors.cpu = 'CPU is required';
      }

      if (!form.memory.trim()) {
        newErrors.memory = 'Memory is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Include any uncommitted draft label from the labels editor
    const finalLabels = { ...form.labels };
    const draft = labelsEditorRef.current?.getDraft();
    if (draft) {
      finalLabels[draft.key] = draft.value;
    }

    // Include any uncommitted draft environment variable from the environment editor
    const finalEnvironment = { ...form.environment };
    const envDraft = environmentEditorRef.current?.getDraft();
    if (envDraft) {
      finalEnvironment[envDraft.key] = envDraft.value;
    }

    // Parse entrypoint and cmd as arrays (space-separated or JSON array format)
    const parseArrayField = (value: string): string[] | undefined => {
      const trimmed = value.trim();
      if (!trimmed) return undefined;
      // Try to parse as JSON array first
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
            return parsed;
          }
        } catch {
          // Fall through to split by spaces
        }
      }
      // Otherwise, split by spaces (respecting quoted strings)
      const matches = trimmed.match(/(?:[^\s"']+|"[^"]*"|'[^']*')+/g);
      return matches?.map((s) => s.replace(/^["']|["']$/g, ''));
    };

    const request: SandboxRequest = {
      labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
      // vmmType is only specified for disk images/pods, not snapshots (snapshot has its own vmmType)
      vmmType: form.sourceType !== 'snapshot' ? form.vmmType : undefined,
      // Entrypoint and cmd are only supported for single-container disk images, not pods or snapshots
      entrypoint:
        form.sourceType !== 'snapshot' && form.sourceType !== 'pod'
          ? parseArrayField(form.entrypoint)
          : undefined,
      cmd:
        form.sourceType !== 'snapshot' && form.sourceType !== 'pod'
          ? parseArrayField(form.cmd)
          : undefined,
      // Environment variables: for single-container disk images only (pods have per-container env)
      environment:
        form.sourceType !== 'snapshot' &&
        form.sourceType !== 'pod' &&
        Object.keys(finalEnvironment).length > 0
          ? finalEnvironment
          : undefined,
      sourcesRef:
        form.sourceType === 'pod'
          ? {
              pod: {
                volumes:
                  form.podVolumes.length > 0
                    ? form.podVolumes.map((v) =>
                        v.kind === 'AzureBlob'
                          ? {
                              kind: 'AzureBlob' as const,
                              name: v.name,
                              readOnly: v.readOnly || undefined,
                              fileCacheSizeLimit: v.fileCacheSizeLimit,
                            }
                          : v.kind === 'DataDisk'
                            ? {
                                kind: 'DataDisk' as const,
                                name: v.name,
                              }
                            : {
                                kind: 'Local' as const,
                                name: v.name,
                                size: v.size,
                              }
                      )
                    : undefined,
                contentPackages:
                  form.podContentPackages.length > 0
                    ? form.podContentPackages.map((cp) => ({
                        contentPackageId: cp.contentPackageId,
                      }))
                    : undefined,
                containers: form.podContainers.map((c) => ({
                  name: c.name.trim(),
                  diskImage:
                    c.diskImageType === 'public'
                      ? { name: c.publicDiskImageId, isPublic: true }
                      : { id: c.diskImageId, isPublic: false },
                  command: parseArrayField(c.command),
                  args: parseArrayField(c.args),
                  env: Object.keys(c.environment).length > 0 ? c.environment : undefined,
                  resources:
                    c.cpuLimit.trim() || c.memoryLimit.trim()
                      ? {
                          limits: {
                            cpu: c.cpuLimit.trim() || undefined,
                            memory: c.memoryLimit.trim() || undefined,
                          },
                        }
                      : undefined,
                  volumeMounts:
                    c.volumeMounts.filter((v) => v.volumeName && v.mountpoint).length > 0
                      ? c.volumeMounts
                          .filter((v) => v.volumeName && v.mountpoint)
                          .map((v) => ({
                            name: v.volumeName,
                            mountPath: v.mountpoint,
                            readOnly: v.readOnly || undefined,
                          }))
                      : undefined,
                  contentPackageDownloads:
                    c.contentPackageDownloads.filter((d) => d.contentPackageId && d.targetPath)
                      .length > 0
                      ? c.contentPackageDownloads
                          .filter((d) => d.contentPackageId && d.targetPath)
                          .map((d) => ({
                            contentPackageId: d.contentPackageId,
                            targetPath: d.targetPath,
                            ...(d.action && d.action !== 'Download' ? { action: d.action } : {}),
                          }))
                      : undefined,
                })),
              },
            }
          : form.sourceType === 'diskImage'
            ? {
                diskImage: {
                  id: form.diskImageId,
                  isPublic: false,
                },
              }
            : form.sourceType === 'publicDiskImage'
              ? {
                  diskImage: {
                    name: form.publicDiskImageId,
                    isPublic: true,
                  },
                }
              : {
                  snapshot: {
                    id: form.snapshotId,
                  },
                },
      // Only include resources when not creating from a snapshot
      // (snapshots have fixed resources that cannot be overridden)
      resources:
        form.sourceType !== 'snapshot'
          ? {
              cpu: form.cpu.trim(),
              memory: form.memory.trim(),
              disk: form.disk.trim() || undefined,
            }
          : undefined,
      // Use explicitly selected connections, or inherit from group if none specified
      connections:
        form.selectedConnections.length > 0
          ? form.selectedConnections
          : selectedGroup?.connections && selectedGroup.connections.length > 0
            ? selectedGroup.connections
            : undefined,
      // Include egress policy if any rules are configured, default is not Allow, or mode is not Legacy
      egressPolicy:
        form.egressHostRules.length > 0 ||
        form.egressRules.length > 0 ||
        form.egressDefaultAction !== 'Allow' ||
        form.trafficInspection !== 'Legacy'
          ? {
              defaultAction: form.egressDefaultAction,
              hostRules: form.egressHostRules
                .filter((r) => r.pattern.trim())
                .map((r) => ({
                  pattern: r.pattern.trim(),
                  action: r.action,
                })),
              rules:
                form.egressRules.length > 0
                  ? form.egressRules
                      .filter((r) => r.match.host.trim())
                      .map((r) => ({
                        ...r,
                        action: {
                          ...r.action,
                          headers: sanitizeEgressHeaders(r.action.headers),
                        },
                      }))
                  : undefined,
              trafficInspection:
                form.trafficInspection !== 'Legacy' ? form.trafficInspection : undefined,
            }
          : undefined,
      // Include sandbox group ID if specified
      sandboxGroupId: form.sandboxGroupId || undefined,
      // Include project ID if coming from Projects page
      projectId: preselectedProjectId || undefined,
      // Include ports if any are specified
      ports:
        form.ports.length > 0
          ? form.ports.map((p: PortEntry) => {
              // Build auth config based on the port's auth settings
              let auth;
              if (!p.auth || p.auth.mode === 'none') {
                auth = { anonymous: true };
              } else if (p.auth.mode === 'github') {
                auth = {
                  github: {
                    enabled: true,
                    emails: p.auth.emails,
                    emailSuffixes: p.auth.emailSuffixes,
                    usernames: p.auth.usernames,
                    usernameSuffixes: p.auth.usernameSuffixes,
                  },
                };
              } else if (p.auth.mode === 'publicGithub') {
                auth = {
                  publicGithub: {
                    enabled: true,
                    emails: p.auth.emails,
                    emailSuffixes: p.auth.emailSuffixes,
                    usernames: p.auth.usernames,
                    usernameSuffixes: p.auth.usernameSuffixes,
                  },
                };
              } else {
                auth = {
                  entraId: {
                    enabled: true,
                    emails: p.auth.emails,
                    emailSuffixes: p.auth.emailSuffixes,
                    objectIds: p.auth.objectIds,
                    tenantIds: p.auth.tenantIds,
                  },
                };
              }
              return {
                port: p.port,
                name: p.name,
                protocol: p.protocol,
                auth,
                ipAccessControl: p.ipAccessControl,
                cors: p.cors,
              };
            })
          : undefined,
      // Always include lifecycle policy - explicitly disable if not enabled
      lifecycle: {
        autoSuspendPolicy:
          // Pod with DataDisk volumes: auto-suspend not yet supported
          hasPodDataDiskVolumes && form.sourceType === 'pod'
            ? { enabled: false }
            : form.autoSuspendEnabled
              ? {
                  enabled: true,
                  interval: parseDuration(form.autoSuspendInterval) ?? 300,
                  // Non-pod with DataDisk volumes: force Disk mode (memory snapshots unsupported)
                  mode: hasNonPodDataDiskVolumes ? 'Disk' : form.autoSuspendMode,
                }
              : {
                  enabled: false,
                },
        autoDeletePolicy: form.autoDeleteEnabled
          ? {
              enabled: true,
              deleteIntervalInSeconds: parseDuration(form.autoDeleteIntervalSeconds) ?? 86400,
            }
          : {
              enabled: false,
            },
      },
      // skipEgressProxy is deprecated — use trafficInspection: 'None' instead
      // Include agent identity if selected
      agentIdentity: form.agentIdentityId
        ? {
            tenantId: agentIdentities?.find((ai) => ai.id === form.agentIdentityId)?.tenantId || '',
            agentId: form.agentIdentityId,
          }
        : undefined,
      // Include volume mounts if any are specified (non-pod only; pods declare volumes in sourcesRef)
      volumes:
        form.sourceType !== 'pod' && form.volumeMounts.length > 0
          ? form.volumeMounts
              .filter((v) => v.volumeName && v.mountpoint)
              .map((v) => ({
                volumeName: v.volumeName,
                mountpoint: v.mountpoint,
                readOnly: v.readOnly || undefined,
              }))
          : undefined,
      // Include telemetry config if any endpoints are configured
      telemetryConfig:
        form.otlpEndpoints.length > 0 ||
        form.logAnalyticsEndpoints.length > 0 ||
        form.logAnalyticsLegacyEndpoints.length > 0
          ? {
              endpoints: [
                ...form.otlpEndpoints.map((e) => ({
                  kind: 'OTLP' as const,
                  endpoint: e.endpoint,
                  protocol: e.protocol,
                  data: e.data,
                  auth: e.auth,
                })),
                ...form.logAnalyticsEndpoints.map((e) => ({
                  kind: 'LogAnalytics' as const,
                  dceEndpoint: e.dceEndpoint,
                  dcrImmutableId: e.dcrImmutableId,
                  tableName: e.tableName,
                  data: e.data,
                })),
                ...form.logAnalyticsLegacyEndpoints.map((e) => ({
                  kind: 'LogAnalyticsLegacy' as const,
                  workspaceId: e.workspaceId,
                  tableName: e.tableName,
                  data: e.data,
                  auth: e.auth,
                })),
              ],
            }
          : undefined,
      // Include content package downloads if any are specified
      contentPackageDownloads:
        form.contentPackageDownloads.length > 0
          ? form.contentPackageDownloads
              .filter((d) => d.contentPackageId && d.targetPath)
              .map((d) => ({
                contentPackageId: d.contentPackageId,
                targetPath: d.targetPath,
                ...(d.action && d.action !== 'Download' ? { action: d.action } : {}),
              }))
          : undefined,
    };

    const startTime = performance.now();
    setCreationError(null); // Clear any previous error
    createMutation.mutate(request, {
      onSuccess: (data) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));
        setCreatedSandbox(data);
        setShowResults(true);
        window.scrollTo({ top: 0, behavior: 'instant' });
      },
      onError: (error) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));

        if (error instanceof ApiException) {
          setCreationError({
            message: error.message,
            detail: error.detail,
            traceId: error.traceId,
            requestId: error.requestId,
            status: error.status,
          });
        } else {
          setCreationError({
            message: error.message || 'An unexpected error occurred',
          });
        }
        setShowResults(true);
        window.scrollTo({ top: 0, behavior: 'instant' });
      },
    });
  };

  const handleViewSandbox = () => {
    if (createdSandbox) {
      const groupId = createdSandbox.sandboxGroupId || form.sandboxGroupId || undefined;
      if (groupId) {
        navigate(
          `/sandbox-groups/${encodeURIComponent(groupId)}/sandboxes/${encodeURIComponent(createdSandbox.id)}`
        );
      } else {
        navigate(`/sandboxes/${encodeURIComponent(createdSandbox.id)}`);
      }
    }
  };

  const handleCreateAnother = () => {
    setShowResults(false);
    setCreatedSandbox(null);
    setTimeTakenMs(0);
    setCreationError(null);
    setForm(initialFormState);
  };

  // When a group is selected, use group-scoped data; otherwise use personal data
  const privateDiskImagesArray = form.sandboxGroupId
    ? groupDiskImages || []
    : privateDiskImages || [];
  const publicDiskImagesArray = publicDiskImages || [];
  const availableSnapshots = form.sandboxGroupId ? groupSnapshots || [] : snapshotsData || [];

  const privateDiskImageOptions = privateDiskImagesArray.map((img) => {
    // Prefer name field, fallback to labels['name'], then show id
    const displayName = img.name || (img.labels && img.labels['name']);
    const baseImage = img.image?.base || 'Built from Dockerfile';
    // Show "Name (id...)" if name exists, or "id - source" if no name
    const label = displayName
      ? `${displayName} (${img.id.substring(0, 8)}...)`
      : `${img.id} - ${baseImage}`;
    return {
      value: img.id,
      label,
    };
  });

  const publicDiskImageOptions = publicDiskImagesArray
    .filter((img) => img.name) // Filter out any images without a name
    .map((img) => ({
      value: img.name,
      label: img.name,
    }));

  const snapshotOptions = availableSnapshots.map((snapshot) => {
    const createdAt = new Date(snapshot.createdAtUtc).toLocaleString();
    const sandboxInfo = snapshot.sandboxId ? ` (Sandbox: ${snapshot.sandboxId})` : '';
    return {
      value: snapshot.id,
      label: `${snapshot.id}${sandboxInfo} - ${createdAt}`,
    };
  });

  // Get the currently selected snapshot (for displaying its resources)
  const selectedSnapshot = form.snapshotId
    ? availableSnapshots.find((s) => s.id === form.snapshotId)
    : undefined;
  // If showing results, display the results page (success or error)
  if (showResults) {
    // Error case
    if (creationError) {
      return (
        <div>
          <PageHeader
            title="Sandbox Creation Failed"
            description="There was an error creating your sandbox"
            breadcrumbs={[
              { label: 'Dashboard', href: '/dashboard' },
              { label: 'Sandboxes', href: '/sandboxes' },
              { label: 'Create' },
            ]}
          />

          <div className="space-y-6">
            <Card>
              <CardHeader title="Error Summary" description="Details about the failure" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="text-sm text-red-600 dark:text-red-400">Status</div>
                    <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                      Failed {creationError.status && `(${creationError.status})`}
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Time Taken</div>
                    <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                      {timeTakenMs.toLocaleString()} ms
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Error
                    </div>
                    <div className="text-red-600 dark:text-red-400 font-medium">
                      {creationError.message}
                    </div>
                  </div>

                  {creationError.detail && (
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Detail
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm">
                        {creationError.detail}
                      </div>
                    </div>
                  )}

                  {(creationError.traceId || creationError.requestId) && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Diagnostic Information
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {creationError.traceId && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Trace ID</div>
                            <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded block mt-1 break-all">
                              {creationError.traceId}
                            </code>
                          </div>
                        )}
                        {creationError.requestId && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Request ID
                            </div>
                            <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded block mt-1 break-all">
                              {creationError.requestId}
                            </code>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Include these IDs when reporting issues to help with troubleshooting.
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardFooter>
                <div className="flex justify-end gap-3">
                  <Link to="/sandboxes">
                    <Button variant="secondary">Back to Sandboxes</Button>
                  </Link>
                  <Button onClick={handleCreateAnother}>Try Again</Button>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      );
    }

    // Success case
    if (createdSandbox) {
      return (
        <div>
          <PageHeader
            title="Sandbox Created"
            description="Your sandbox has been created successfully"
            breadcrumbs={[
              { label: 'Dashboard', href: '/dashboard' },
              { label: 'Sandboxes', href: '/sandboxes' },
              { label: 'Create' },
            ]}
          />

          {/* Action buttons at the top */}
          <div className="flex justify-end gap-3 mb-6">
            <Button variant="secondary" onClick={handleCreateAnother}>
              Create Another
            </Button>
            <Button onClick={handleViewSandbox}>View Sandbox</Button>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader title="Creation Summary" description="Sandbox creation details" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-sm text-green-700 dark:text-green-400">Status</div>
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                      Success
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm text-blue-700 dark:text-blue-400">Time Taken</div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {timeTakenMs.toLocaleString()} ms
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Sandbox Details" description="Details of the created sandbox" />
              <CardBody>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">ID</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdSandbox.id}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Labels</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdSandbox.labels && Object.keys(createdSandbox.labels).length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(createdSandbox.labels as Record<string, string>).map(
                            ([key, value]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                              >
                                {key}: {value}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">VMM Type</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {createdSandbox.vmmType || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Resources</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      CPU: {createdSandbox.resources?.cpu || 'N/A'}, Memory:{' '}
                      {createdSandbox.resources?.memory || 'N/A'}
                      {createdSandbox.resources?.disk && `, Disk: ${createdSandbox.resources.disk}`}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Waterfall Chart - collapsible, shown when traces are available */}
            {createdSandbox.debug?.traces && createdSandbox.debug.traces.length > 0 && (
              <details className="group">
                <summary className="cursor-pointer list-none">
                  <Card>
                    <CardHeader
                      title={
                        <span className="flex items-center gap-2">
                          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                          Performance Waterfall
                        </span>
                      }
                      description="OpenTelemetry-style trace visualization showing timing across all services"
                    />
                  </Card>
                </summary>
                <Card className="mt-0 rounded-t-none border-t-0">
                  <CardBody>
                    <WaterfallChart
                      traces={createdSandbox.debug.traces}
                      traceId={createdSandbox.debug.traceId}
                      clientDurationMs={timeTakenMs}
                    />
                  </CardBody>
                </Card>
              </details>
            )}
          </div>
        </div>
      );
    }
  }

  // Normal form view
  return (
    <div>
      <PageHeader
        title="Create Sandbox"
        description="Create a new sandbox from a disk image or snapshot"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Sandboxes', href: '/sandboxes' },
          { label: 'Create' },
        ]}
        action={
          <Link to="/sandboxes">
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source Selection */}
          <Card className="lg:col-span-2">
            <CardHeader
              title="Source"
              description="Select the source type and choose from available options"
            />
            <CardBody>
              {/* Radio buttons for source type */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Source Type
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="publicDiskImage"
                      checked={form.sourceType === 'publicDiskImage'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                      Public Disk Image
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="diskImage"
                      checked={form.sourceType === 'diskImage'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                      Disk Image
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="snapshot"
                      checked={form.sourceType === 'snapshot'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Snapshot</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="pod"
                      checked={form.sourceType === 'pod'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Pod</span>
                    <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      preview
                    </span>
                  </label>
                </div>
              </div>

              {/* Conditional rendering based on source type */}
              {form.sourceType === 'publicDiskImage' ? (
                <>
                  <Select
                    label="Public Disk Image"
                    options={publicDiskImageOptions}
                    value={form.publicDiskImageId}
                    onChange={(e) => handleChange('publicDiskImageId', e.target.value)}
                    error={errors.publicDiskImageId}
                    placeholder={
                      diskImagesLoading
                        ? 'Loading public disk images...'
                        : 'Select a public disk image'
                    }
                    disabled={diskImagesLoading}
                  />
                  {publicDiskImageOptions.length === 0 && !diskImagesLoading && (
                    <p className="mt-2 text-sm text-warning-500">
                      No public disk images available.
                    </p>
                  )}
                </>
              ) : form.sourceType === 'diskImage' ? (
                <>
                  <Select
                    label="Disk Image"
                    options={privateDiskImageOptions}
                    value={form.diskImageId}
                    onChange={(e) => handleChange('diskImageId', e.target.value)}
                    error={errors.diskImageId}
                    placeholder={
                      diskImagesLoading ? 'Loading disk images...' : 'Select a disk image'
                    }
                    disabled={diskImagesLoading}
                  />
                  {privateDiskImageOptions.length === 0 && !diskImagesLoading && (
                    <p className="mt-2 text-sm text-warning-500">
                      No disk images available. Please create a disk image first.
                    </p>
                  )}
                </>
              ) : form.sourceType === 'snapshot' ? (
                <>
                  <Select
                    label="Snapshot"
                    options={snapshotOptions}
                    value={form.snapshotId}
                    onChange={(e) => handleChange('snapshotId', e.target.value)}
                    error={errors.snapshotId}
                    placeholder={snapshotsLoading ? 'Loading snapshots...' : 'Select a snapshot'}
                    disabled={snapshotsLoading}
                  />
                  {availableSnapshots.length === 0 && !snapshotsLoading && (
                    <p className="mt-2 text-sm text-warning-500">No snapshots available.</p>
                  )}
                </>
              ) : (
                /* Pod (Multi-Container) source */
                <div className="space-y-4">
                  {/* Pod-level Volumes */}
                  <div className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg border-l-4 border-l-amber-500 dark:border-l-amber-500">
                    <button
                      type="button"
                      onClick={() => setPodVolumeSectionExpanded(!podVolumeSectionExpanded)}
                      className={`w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors rounded-t-lg ${podVolumeSectionExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-amber-500 dark:text-amber-400">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <ellipse cx="12" cy="5" rx="9" ry="3" />
                            <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                            <path d="M3 12A9 3 0 0 0 21 12" />
                          </svg>
                        </span>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Pod Volumes
                        </h4>
                        {form.podVolumes.length > 0 && (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                            {form.podVolumes.length}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${podVolumeSectionExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {podVolumeSectionExpanded && (
                      <div className="px-4 py-3 space-y-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Declare shared volumes that containers can mount. Containers specify where
                          to mount them. Total local space (blobfuse cache + local volumes) cannot
                          exceed 1 GB.
                        </p>
                        {(() => {
                          const parseSize = (s: string): number => {
                            const m = s.match(/^(\d+(?:\.\d+)?)(Mi|Gi)$/);
                            if (!m) return 0;
                            const val = parseFloat(m[1]);
                            return m[2] === 'Gi' ? val * 1024 : val;
                          };
                          const totalMB = form.podVolumes.reduce((sum, v) => {
                            if (v.kind === 'Local') return sum + parseSize(v.size);
                            if (v.kind === 'AzureBlob' && v.fileCacheSizeLimit)
                              return sum + parseSize(v.fileCacheSizeLimit);
                            // DataDisk volumes do NOT count toward local space budget
                            return sum;
                          }, 0);
                          return totalMB > 1024 ? (
                            <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                              Warning: Total local space ({totalMB} MB) exceeds the 1 GB limit. The
                              server will reject this request.
                            </p>
                          ) : null;
                        })()}
                        {form.podVolumes.map((vol, vIdx) => (
                          <div
                            key={vIdx}
                            className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {vol.name}
                              </span>
                              <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded font-medium">
                                {vol.kind === 'AzureBlob'
                                  ? 'azureBlob'
                                  : vol.kind === 'DataDisk'
                                    ? 'dataDisk'
                                    : 'local'}
                              </span>
                              {vol.kind === 'AzureBlob' && (
                                <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded font-medium">
                                  {vol.readOnly ? 'read-only' : 'read-write'}
                                </span>
                              )}
                              {vol.kind === 'AzureBlob' && vol.fileCacheSizeLimit && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                                  cache: {vol.fileCacheSizeLimit}
                                </span>
                              )}
                              {vol.kind === 'Local' && (
                                <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded font-medium">
                                  {vol.size}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const removed = form.podVolumes[vIdx];
                                setForm((prev) => ({
                                  ...prev,
                                  podVolumes: prev.podVolumes.filter((_, i) => i !== vIdx),
                                  // Also remove any container mounts referencing this volume
                                  podContainers: prev.podContainers.map((c) => ({
                                    ...c,
                                    volumeMounts: c.volumeMounts.filter(
                                      (m) => m.volumeName !== removed.name
                                    ),
                                  })),
                                }));
                              }}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              title="Remove volume"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        {/* DataDisk lifecycle constraint notice */}
                        {hasPodDataDiskVolumes && (
                          <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <Info className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              Auto-suspend has been <strong>disabled</strong> because suspend is not
                              yet supported for pod sandboxes with data disk volumes.
                            </p>
                          </div>
                        )}
                        {/* Add volume form */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPodVolumeDraftKind('AzureBlob');
                                setPodVolumeDraftName('');
                              }}
                              className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                                podVolumeDraftKind === 'AzureBlob'
                                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                              }`}
                            >
                              Azure Blob
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPodVolumeDraftKind('Local');
                                setPodVolumeDraftName('');
                              }}
                              className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                                podVolumeDraftKind === 'Local'
                                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                              }`}
                            >
                              Local
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPodVolumeDraftKind('DataDisk');
                                setPodVolumeDraftName('');
                              }}
                              className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                                podVolumeDraftKind === 'DataDisk'
                                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                              }`}
                            >
                              Data Disk
                            </button>
                          </div>
                          <div className="flex items-center gap-3">
                            {podVolumeDraftKind === 'AzureBlob' ? (
                              <div className="flex-1">
                                <Select
                                  options={
                                    volumesData
                                      ?.filter(
                                        (v) =>
                                          v.type !== 'DataDisk' &&
                                          !form.podVolumes.some((pv) => pv.name === v.volumeName)
                                      )
                                      .map((v) => ({
                                        value: v.volumeName,
                                        label: v.volumeName,
                                      })) ?? []
                                  }
                                  value={podVolumeDraftName}
                                  onChange={(e) => setPodVolumeDraftName(e.target.value)}
                                  placeholder={
                                    volumesLoading ? 'Loading...' : 'Select a volume to declare'
                                  }
                                  disabled={volumesLoading}
                                />
                              </div>
                            ) : podVolumeDraftKind === 'DataDisk' ? (
                              <div className="flex-1">
                                <Select
                                  options={
                                    volumesData
                                      ?.filter(
                                        (v) =>
                                          v.type === 'DataDisk' &&
                                          !v.isAttached &&
                                          !form.podVolumes.some((pv) => pv.name === v.volumeName)
                                      )
                                      .map((v) => ({
                                        value: v.volumeName,
                                        label: `${v.volumeName}${v.size ? ` (${v.size})` : ''}`,
                                      })) ?? []
                                  }
                                  value={podVolumeDraftName}
                                  onChange={(e) => setPodVolumeDraftName(e.target.value)}
                                  placeholder={
                                    volumesLoading ? 'Loading...' : 'Select a data disk volume'
                                  }
                                  disabled={volumesLoading}
                                />
                              </div>
                            ) : (
                              <>
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    value={podVolumeDraftName}
                                    onChange={(e) => setPodVolumeDraftName(e.target.value)}
                                    placeholder="Volume name"
                                    className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-[#333] bg-white dark:bg-[#161616] text-gray-900 dark:text-white"
                                  />
                                </div>
                                <div className="w-24">
                                  <input
                                    type="text"
                                    value={podVolumeDraftSize}
                                    onChange={(e) => setPodVolumeDraftSize(e.target.value)}
                                    placeholder="256Mi"
                                    title="Local volume size (e.g. 256Mi, 512Mi)"
                                    className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-[#333] bg-white dark:bg-[#161616] text-gray-900 dark:text-white"
                                  />
                                </div>
                              </>
                            )}
                            {podVolumeDraftKind === 'AzureBlob' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setPodVolumeDraftReadOnly(!podVolumeDraftReadOnly)}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                                    podVolumeDraftReadOnly
                                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                      : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500'
                                  }`}
                                  title={
                                    podVolumeDraftReadOnly
                                      ? 'Click to make read-write'
                                      : 'Click to make read-only'
                                  }
                                >
                                  {podVolumeDraftReadOnly ? (
                                    <CheckCircle className="h-3.5 w-3.5" />
                                  ) : (
                                    <Circle className="h-3.5 w-3.5" />
                                  )}
                                  RO
                                </button>
                                <input
                                  type="text"
                                  value={podVolumeDraftCacheSize}
                                  onChange={(e) => setPodVolumeDraftCacheSize(e.target.value)}
                                  placeholder="Cache (e.g. 256Mi)"
                                  title="Blobfuse2 file cache size limit (optional)"
                                  className="w-32 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-[#333] bg-white dark:bg-[#161616] text-gray-900 dark:text-white"
                                />
                              </>
                            )}
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              leftIcon={<Plus className="h-3.5 w-3.5" />}
                              disabled={
                                !podVolumeDraftName ||
                                (podVolumeDraftKind === 'AzureBlob' && !podVolumeDraftCacheSize) ||
                                (podVolumeDraftKind === 'Local' && !podVolumeDraftSize)
                              }
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  podVolumes: [
                                    ...prev.podVolumes,
                                    {
                                      name: podVolumeDraftName,
                                      kind: podVolumeDraftKind,
                                      readOnly: podVolumeDraftReadOnly,
                                      fileCacheSizeLimit: podVolumeDraftCacheSize,
                                      size: podVolumeDraftSize,
                                    },
                                  ],
                                }));
                                setPodVolumeDraftName('');
                                setPodVolumeDraftReadOnly(false);
                                setPodVolumeDraftSize('256Mi');
                                setPodVolumeDraftCacheSize('256Mi');
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pod-level Content Packages */}
                  <div className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg border-l-4 border-l-emerald-500 dark:border-l-emerald-500">
                    <button
                      type="button"
                      onClick={() =>
                        setPodContentPackageSectionExpanded(!podContentPackageSectionExpanded)
                      }
                      className={`w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors rounded-t-lg ${podContentPackageSectionExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-500 dark:text-emerald-400">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m7.5 4.27 9 5.15" />
                            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                            <path d="m3.3 7 8.7 5 8.7-5" />
                            <path d="M12 22V12" />
                          </svg>
                        </span>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Pod Content Packages
                        </h4>
                        {form.podContentPackages.length > 0 && (
                          <span className="text-xs px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full font-medium">
                            {form.podContentPackages.length}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${podContentPackageSectionExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {podContentPackageSectionExpanded && (
                      <div className="px-4 py-3 space-y-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Declare content packages the pod uses. Containers specify where to
                          download them.
                        </p>
                        {form.podContentPackages.map((cp, cpIdx) => {
                          return (
                            <div
                              key={cpIdx}
                              className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                            >
                              <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {cp.contentPackageId}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const removed = form.podContentPackages[cpIdx];
                                  setForm((prev) => ({
                                    ...prev,
                                    podContentPackages: prev.podContentPackages.filter(
                                      (_, i) => i !== cpIdx
                                    ),
                                    // Also remove per-container downloads referencing this package
                                    podContainers: prev.podContainers.map((c) => ({
                                      ...c,
                                      contentPackageDownloads: c.contentPackageDownloads.filter(
                                        (d) => d.contentPackageId !== removed.contentPackageId
                                      ),
                                    })),
                                  }));
                                }}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                title="Remove content package"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                        {/* Add content package form */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Select
                              options={
                                contentPackagesData
                                  ?.filter(
                                    (p) =>
                                      !form.podContentPackages.some(
                                        (pp) => pp.contentPackageId === p.id
                                      )
                                  )
                                  .map((p) => ({
                                    value: p.id,
                                    label: p.id,
                                  })) ?? []
                              }
                              value={podContentPackageDraftId}
                              onChange={(e) => setPodContentPackageDraftId(e.target.value)}
                              placeholder={
                                contentPackagesLoading
                                  ? 'Loading...'
                                  : 'Select a content package to declare'
                              }
                              disabled={contentPackagesLoading}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            leftIcon={<Plus className="h-3.5 w-3.5" />}
                            disabled={!podContentPackageDraftId}
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                podContentPackages: [
                                  ...prev.podContentPackages,
                                  { contentPackageId: podContentPackageDraftId },
                                ],
                              }));
                              setPodContentPackageDraftId('');
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Containers */}
                  {form.podContainers.map((container, idx) => {
                    const borderColors = [
                      'border-l-blue-500 dark:border-l-blue-500',
                      'border-l-purple-500 dark:border-l-purple-500',
                      'border-l-sky-500 dark:border-l-sky-500',
                      'border-l-rose-500 dark:border-l-rose-500',
                      'border-l-indigo-500 dark:border-l-indigo-500',
                      'border-l-cyan-500 dark:border-l-cyan-500',
                      'border-l-pink-500 dark:border-l-pink-500',
                      'border-l-orange-500 dark:border-l-orange-500',
                      'border-l-lime-500 dark:border-l-lime-500',
                      'border-l-violet-500 dark:border-l-violet-500',
                    ];
                    const iconColors = [
                      'text-blue-500 dark:text-blue-400',
                      'text-purple-500 dark:text-purple-400',
                      'text-sky-500 dark:text-sky-400',
                      'text-rose-500 dark:text-rose-400',
                      'text-indigo-500 dark:text-indigo-400',
                      'text-cyan-500 dark:text-cyan-400',
                      'text-pink-500 dark:text-pink-400',
                      'text-orange-500 dark:text-orange-400',
                      'text-lime-500 dark:text-lime-400',
                      'text-violet-500 dark:text-violet-400',
                    ];
                    const updateContainer = (
                      field: keyof PodContainerFormState,
                      value: unknown
                    ) => {
                      setForm((prev) => ({
                        ...prev,
                        podContainers: prev.podContainers.map((c, i) =>
                          i === idx ? { ...c, [field]: value } : c
                        ),
                      }));
                    };
                    return (
                      <div
                        key={idx}
                        className={`border border-gray-200 dark:border-[#2a2a2a] rounded-lg border-l-4 ${borderColors[idx] ?? 'border-l-gray-500'}`}
                      >
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] rounded-t-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={iconColors[idx] ?? 'text-gray-500 dark:text-gray-400'}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 12.999V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1" />
                                <path d="M22 7H2" />
                                <path d="M5 2v5" />
                                <path d="M19 2v5" />
                              </svg>
                            </span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {container.name ? `${container.name}` : `Container ${idx + 1}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {editingContainerIdx === idx ? (
                              <button
                                type="button"
                                onClick={() => setEditingContainerIdx(-1)}
                                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                              >
                                Done
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setEditingContainerIdx(idx)}
                                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                              >
                                Edit
                              </button>
                            )}
                            {form.podContainers.length > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    podContainers: prev.podContainers.filter((_, i) => i !== idx),
                                  }))
                                }
                                className="text-gray-400 hover:text-danger-500 transition-colors"
                                title="Remove container"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Collapsed read-only summary */}
                        {editingContainerIdx !== idx && (
                          <div
                            className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#111] transition-colors"
                            onClick={() => setEditingContainerIdx(idx)}
                          >
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                              {(container.diskImageType === 'public'
                                ? container.publicDiskImageId
                                : container.diskImageId) && (
                                <span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    Image:
                                  </span>{' '}
                                  {container.diskImageType === 'public'
                                    ? container.publicDiskImageId
                                    : container.diskImageId.substring(0, 12) + '...'}
                                </span>
                              )}
                              {container.command && (
                                <span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    Entrypoint:
                                  </span>{' '}
                                  <code className="text-xs">{container.command}</code>
                                </span>
                              )}
                              {Object.keys(container.environment).length > 0 && (
                                <span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    Env:
                                  </span>{' '}
                                  {Object.keys(container.environment).length} var(s)
                                </span>
                              )}
                              {container.volumeMounts.length > 0 && (
                                <span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    Volumes:
                                  </span>{' '}
                                  {container.volumeMounts.length} mount(s)
                                </span>
                              )}
                              {container.contentPackageDownloads.length > 0 && (
                                <span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    Packages:
                                  </span>{' '}
                                  {container.contentPackageDownloads.length}
                                </span>
                              )}
                              {(container.cpuLimit || container.memoryLimit) && (
                                <span>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">
                                    Limits:
                                  </span>{' '}
                                  {[container.cpuLimit, container.memoryLimit]
                                    .filter(Boolean)
                                    .join(' / ')}
                                </span>
                              )}
                              {!(container.diskImageType === 'public'
                                ? container.publicDiskImageId
                                : container.diskImageId) &&
                                !container.command &&
                                Object.keys(container.environment).length === 0 &&
                                container.volumeMounts.length === 0 &&
                                container.contentPackageDownloads.length === 0 &&
                                !container.cpuLimit &&
                                !container.memoryLimit && (
                                  <span className="italic">Not configured — click to edit</span>
                                )}
                            </div>
                          </div>
                        )}

                        {/* Expanded editor */}
                        {editingContainerIdx === idx && (
                          <div className="px-4 py-3 space-y-3">
                            {/* Container Name — always visible */}
                            <Input
                              label="Container Name"
                              placeholder="e.g., webserver, sidecar, redis"
                              value={container.name}
                              onChange={(e) => updateContainer('name', e.target.value)}
                              helperText="DNS label format: lowercase letters, digits, hyphens (max 63 chars)"
                              required
                            />

                            {/* Tab strip */}
                            {(() => {
                              const activeTab = podContainerTabs[idx] ?? 'image';
                              const setTab = (tab: string) =>
                                setPodContainerTabs((prev) => ({ ...prev, [idx]: tab }));
                              const tabs = [
                                { id: 'image', label: 'Image' },
                                { id: 'entrypoint', label: 'Entrypoint' },
                                { id: 'env', label: 'Environment' },
                                { id: 'volumes', label: 'Volumes' },
                                { id: 'packages', label: 'Packages' },
                                { id: 'limits', label: 'Limits' },
                              ];
                              return (
                                <>
                                  <div className="flex gap-1 border-b border-gray-200 dark:border-[#2a2a2a]">
                                    {tabs.map((tab) => (
                                      <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setTab(tab.id)}
                                        className={`px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
                                          activeTab === tab.id
                                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                        }`}
                                      >
                                        {tab.label}
                                      </button>
                                    ))}
                                  </div>

                                  {/* Image tab */}
                                  {activeTab === 'image' && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Disk Image
                                      </label>
                                      <div className="flex gap-4 mb-2">
                                        <label className="flex items-center cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`container-${idx}-diskImageType`}
                                            value="public"
                                            checked={container.diskImageType === 'public'}
                                            onChange={() =>
                                              updateContainer('diskImageType', 'public')
                                            }
                                            className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300"
                                          />
                                          <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            Public
                                          </span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`container-${idx}-diskImageType`}
                                            value="private"
                                            checked={container.diskImageType === 'private'}
                                            onChange={() =>
                                              updateContainer('diskImageType', 'private')
                                            }
                                            className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300"
                                          />
                                          <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            Private
                                          </span>
                                        </label>
                                      </div>
                                      {container.diskImageType === 'public' ? (
                                        <Select
                                          options={publicDiskImageOptions}
                                          value={container.publicDiskImageId}
                                          onChange={(e) =>
                                            updateContainer('publicDiskImageId', e.target.value)
                                          }
                                          placeholder={
                                            diskImagesLoading
                                              ? 'Loading...'
                                              : 'Select a public disk image'
                                          }
                                          disabled={diskImagesLoading}
                                        />
                                      ) : (
                                        <Select
                                          options={privateDiskImageOptions}
                                          value={container.diskImageId}
                                          onChange={(e) =>
                                            updateContainer('diskImageId', e.target.value)
                                          }
                                          placeholder={
                                            diskImagesLoading ? 'Loading...' : 'Select a disk image'
                                          }
                                          disabled={diskImagesLoading}
                                        />
                                      )}
                                    </div>
                                  )}

                                  {/* Entrypoint tab */}
                                  {activeTab === 'entrypoint' && (
                                    <div className="grid grid-cols-2 gap-3">
                                      <Input
                                        label="Entrypoint"
                                        placeholder='/bin/sleep or ["/bin/sleep"]'
                                        value={container.command}
                                        onChange={(e) => updateContainer('command', e.target.value)}
                                        helperText="Container entrypoint (space-separated or JSON array)"
                                      />
                                      <Input
                                        label="Cmd"
                                        placeholder='infinity or ["infinity"]'
                                        value={container.args}
                                        onChange={(e) => updateContainer('args', e.target.value)}
                                        helperText="Command arguments (space-separated or JSON array)"
                                      />
                                    </div>
                                  )}

                                  {/* Env tab */}
                                  {activeTab === 'env' && (
                                    <LabelsEditor
                                      value={container.environment}
                                      onChange={(env) => updateContainer('environment', env)}
                                      label="Environment Variables (Optional)"
                                      helperText="Add environment variables to set in the container. These override image defaults."
                                      keyPlaceholder="Variable name (e.g., MY_VAR)"
                                      valuePlaceholder="Value"
                                      supportSecrets
                                    />
                                  )}

                                  {/* Volumes tab */}
                                  {activeTab === 'volumes' && (
                                    <div className="space-y-2">
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Volumes
                                      </label>
                                      {/* Committed entries */}
                                      {container.volumeMounts.map((mount, mIdx) => (
                                        <div
                                          key={mIdx}
                                          className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                                        >
                                          <div className="flex items-center gap-3 min-w-0">
                                            <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                              {mount.volumeName}
                                            </span>
                                            <span className="text-gray-400 dark:text-gray-500">
                                              →
                                            </span>
                                            <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#222] px-1.5 py-0.5 rounded truncate">
                                              {mount.mountpoint}
                                            </code>
                                            <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded font-medium">
                                              {mount.readOnly ? 'read-only' : 'read-write'}
                                            </span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              updateContainer(
                                                'volumeMounts',
                                                container.volumeMounts.filter((_, i) => i !== mIdx)
                                              );
                                            }}
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                            title="Remove volume mount"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                      {/* Always-visible draft form */}
                                      {(() => {
                                        const defaultDraft = {
                                          volumeName: '',
                                          mountpoint: '',
                                          readOnly: false,
                                          visible: true,
                                        };
                                        const draft = podVolumeDrafts[idx] ?? defaultDraft;
                                        // Pod containers can only mount volumes declared at pod level
                                        const availableVols = form.podVolumes.filter(
                                          (v) =>
                                            !container.volumeMounts.some(
                                              (m) => m.volumeName === v.name
                                            )
                                        );
                                        if (form.podVolumes.length === 0) {
                                          return (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              No pod-level volumes declared. Add volumes in the
                                              &quot;Pod Volumes&quot; section above.
                                            </p>
                                          );
                                        }
                                        if (availableVols.length === 0) {
                                          return container.volumeMounts.length === 0 ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              No pod-level volumes declared. Add volumes in the
                                              &quot;Pod Volumes&quot; section above.
                                            </p>
                                          ) : (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              All declared pod volumes have been mounted.
                                            </p>
                                          );
                                        }
                                        return (
                                          <div className="flex items-start gap-3">
                                            <div className="flex-1">
                                              <Select
                                                options={availableVols?.map((v) => ({
                                                  value: v.name,
                                                  label: v.name,
                                                }))}
                                                value={draft.volumeName}
                                                onChange={(e) =>
                                                  setPodVolumeDrafts((prev) => ({
                                                    ...prev,
                                                    [idx]: {
                                                      ...(prev[idx] ?? defaultDraft),
                                                      volumeName: e.target.value,
                                                    },
                                                  }))
                                                }
                                                placeholder="Select a pod volume"
                                              />
                                            </div>
                                            <div className="flex-1">
                                              <Input
                                                placeholder="/mnt/data"
                                                value={draft.mountpoint}
                                                onChange={(e) => {
                                                  setPodVolumeDrafts((prev) => ({
                                                    ...prev,
                                                    [idx]: {
                                                      ...(prev[idx] ?? defaultDraft),
                                                      mountpoint: e.target.value,
                                                    },
                                                  }));
                                                  if (podMountPathErrors[idx])
                                                    setPodMountPathErrors((prev) => ({
                                                      ...prev,
                                                      [idx]: undefined,
                                                    }));
                                                }}
                                                onBlur={() => {
                                                  let cleaned = draft.mountpoint.trim();
                                                  if (cleaned && !cleaned.startsWith('/')) {
                                                    cleaned = '/' + cleaned;
                                                  }
                                                  while (
                                                    cleaned.length > 1 &&
                                                    cleaned.endsWith('/')
                                                  ) {
                                                    cleaned = cleaned.slice(0, -1);
                                                  }
                                                  if (cleaned !== draft.mountpoint) {
                                                    setPodVolumeDrafts((prev) => ({
                                                      ...prev,
                                                      [idx]: {
                                                        ...(prev[idx] ?? defaultDraft),
                                                        mountpoint: cleaned,
                                                      },
                                                    }));
                                                  }
                                                }}
                                                error={podMountPathErrors[idx]}
                                              />
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setPodVolumeDrafts((prev) => ({
                                                    ...prev,
                                                    [idx]: {
                                                      ...(prev[idx] ?? defaultDraft),
                                                      readOnly: !(prev[idx] ?? defaultDraft)
                                                        .readOnly,
                                                    },
                                                  }))
                                                }
                                                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                                                  draft.readOnly
                                                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                                    : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500'
                                                }`}
                                                title={
                                                  draft.readOnly
                                                    ? 'Click to make read-write'
                                                    : 'Click to make read-only'
                                                }
                                              >
                                                {draft.readOnly ? (
                                                  <CheckCircle className="h-3.5 w-3.5" />
                                                ) : (
                                                  <Circle className="h-3.5 w-3.5" />
                                                )}
                                                Read-only
                                              </button>
                                              <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                leftIcon={<Plus className="h-3.5 w-3.5" />}
                                                disabled={
                                                  !draft.volumeName || !draft.mountpoint.trim()
                                                }
                                                onClick={() => {
                                                  let mountpoint = draft.mountpoint
                                                    .trim()
                                                    .startsWith('/')
                                                    ? draft.mountpoint.trim()
                                                    : '/' + draft.mountpoint.trim();
                                                  while (
                                                    mountpoint.length > 1 &&
                                                    mountpoint.endsWith('/')
                                                  ) {
                                                    mountpoint = mountpoint.slice(0, -1);
                                                  }
                                                  if (mountpoint === '/') {
                                                    setPodMountPathErrors((prev) => ({
                                                      ...prev,
                                                      [idx]: 'Cannot mount at root /',
                                                    }));
                                                    return;
                                                  }
                                                  if (/\0/.test(mountpoint)) {
                                                    setPodMountPathErrors((prev) => ({
                                                      ...prev,
                                                      [idx]: 'Path contains invalid characters',
                                                    }));
                                                    return;
                                                  }
                                                  if (/\/\//.test(mountpoint)) {
                                                    setPodMountPathErrors((prev) => ({
                                                      ...prev,
                                                      [idx]: 'Path contains consecutive slashes',
                                                    }));
                                                    return;
                                                  }
                                                  if (/[\s]/.test(mountpoint)) {
                                                    setPodMountPathErrors((prev) => ({
                                                      ...prev,
                                                      [idx]: 'Path should not contain whitespace',
                                                    }));
                                                    return;
                                                  }
                                                  setPodMountPathErrors((prev) => ({
                                                    ...prev,
                                                    [idx]: undefined,
                                                  }));
                                                  updateContainer('volumeMounts', [
                                                    ...container.volumeMounts,
                                                    {
                                                      volumeName: draft.volumeName,
                                                      mountpoint,
                                                      readOnly: draft.readOnly,
                                                    },
                                                  ]);
                                                  setPodVolumeDrafts((prev) => ({
                                                    ...prev,
                                                    [idx]: {
                                                      volumeName: '',
                                                      mountpoint: '',
                                                      readOnly: false,
                                                      visible: true,
                                                    },
                                                  }));
                                                }}
                                              >
                                                Add
                                              </Button>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* Packages tab */}
                                  {activeTab === 'packages' && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Content Package Downloads
                                      </label>
                                      {form.podContentPackages.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                          No pod-level content packages declared. Add packages in
                                          the &quot;Pod Content Packages&quot; section above.
                                        </p>
                                      ) : (
                                        <ContentPackageDownloadsEditor
                                          value={container.contentPackageDownloads}
                                          onChange={(downloads) =>
                                            updateContainer('contentPackageDownloads', downloads)
                                          }
                                          contentPackages={contentPackagesData?.filter((p) =>
                                            form.podContentPackages.some(
                                              (pp) => pp.contentPackageId === p.id
                                            )
                                          )}
                                          isLoading={contentPackagesLoading}
                                          alwaysShowForm
                                        />
                                      )}
                                    </div>
                                  )}

                                  {/* Limits tab */}
                                  {activeTab === 'limits' && (
                                    <div className="grid grid-cols-2 gap-3">
                                      <Input
                                        label="CPU Limit"
                                        placeholder="e.g., 500m"
                                        value={container.cpuLimit}
                                        onChange={(e) =>
                                          updateContainer('cpuLimit', e.target.value)
                                        }
                                        helperText="CPU in millicores (e.g., 500m = 0.5 cores)"
                                      />
                                      <Input
                                        label="Memory Limit"
                                        placeholder="e.g., 256Mi"
                                        value={container.memoryLimit}
                                        onChange={(e) =>
                                          updateContainer('memoryLimit', e.target.value)
                                        }
                                        helperText="Memory in Mi or Gi (e.g., 256Mi)"
                                      />
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {form.podContainers.length < 10 && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          podContainers: [...prev.podContainers, createEmptyPodContainer()],
                        }));
                        setEditingContainerIdx(form.podContainers.length);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Container ({10 - form.podContainers.length} remaining)
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Additional Details - Collapsible */}
          <Card className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setAdditionalDetailsExpanded(!additionalDetailsExpanded)}
              className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${additionalDetailsExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                  Additional Details
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                  {form.sourceType === 'pod'
                    ? 'Configure labels, VMM type, and connections'
                    : 'Configure labels, VMM type, entrypoint, and environment variables'}
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${additionalDetailsExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${additionalDetailsExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <CardBody>
                  <div className="space-y-4">
                    <div>
                      <LabelsEditor
                        ref={labelsEditorRef}
                        value={form.labels}
                        onChange={(labels) => setForm((prev) => ({ ...prev, labels }))}
                        helperText="Add key-value pairs to label this sandbox"
                      />
                    </div>
                    {/* Agent Identity selection - only in preview mode */}
                    {isPreviewMode && (
                      <div>
                        <Select
                          label="Agent Identity (optional)"
                          options={[
                            { value: '', label: 'None' },
                            ...(agentIdentities?.map((identity) => ({
                              value: identity.id,
                              label: `${identity.displayName} (${identity.id})`,
                            })) || []),
                          ]}
                          value={form.agentIdentityId}
                          onChange={(e) => handleChange('agentIdentityId', e.target.value)}
                          disabled={agentIdentitiesLoading}
                          placeholder={
                            agentIdentitiesLoading
                              ? 'Loading agent identities...'
                              : 'Select an agent identity'
                          }
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Assign an agent identity to enable the sandbox to authenticate to Azure
                          resources.
                        </p>
                      </div>
                    )}
                    {/* VMM Type selector - Firecracker shown only with ?show_firecracker=true */}
                    {showFirecracker &&
                      (form.sourceType === 'diskImage' ||
                        form.sourceType === 'publicDiskImage') && (
                        <Select
                          label="VMM Type"
                          options={[
                            { value: 'CloudHypervisor', label: 'Cloud Hypervisor' },
                            { value: 'Firecracker', label: 'Firecracker' },
                          ]}
                          value={form.vmmType}
                          onChange={(e) => handleChange('vmmType', e.target.value as VmmType)}
                        />
                      )}
                    {(form.sourceType === 'diskImage' || form.sourceType === 'publicDiskImage') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Entrypoint"
                          placeholder='/bin/sleep or ["/bin/sleep"]'
                          value={form.entrypoint}
                          onChange={(e) => handleChange('entrypoint', e.target.value)}
                          helperText="Container entrypoint (space-separated or JSON array)"
                        />
                        <Input
                          label="Cmd"
                          placeholder='infinity or ["infinity"]'
                          value={form.cmd}
                          onChange={(e) => handleChange('cmd', e.target.value)}
                          helperText="Command arguments (space-separated or JSON array)"
                        />
                      </div>
                    )}

                    {/* Environment Variables - only shown for disk images, not snapshots */}
                    {(form.sourceType === 'diskImage' || form.sourceType === 'publicDiskImage') && (
                      <div>
                        <LabelsEditor
                          ref={environmentEditorRef}
                          value={form.environment}
                          onChange={(environment) => setForm((prev) => ({ ...prev, environment }))}
                          label="Environment Variables (Optional)"
                          helperText="Add environment variables to set in the container. These override image defaults."
                          keyPlaceholder="Variable name (e.g., MY_VAR)"
                          valuePlaceholder="Value"
                          supportSecrets
                        />
                      </div>
                    )}

                    {/* Connections dropdown */}
                    <div>
                      <Select
                        label="Connections (optional)"
                        options={connections.map((conn) => ({
                          value: conn.id,
                          label: `${conn.name} (${conn.type})`,
                        }))}
                        value={form.selectedConnections[0] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            // Check if this is a personal connector being added with incompatible ports
                            const conn = connections.find((c) => c.id === value);
                            if (
                              conn &&
                              PERSONAL_CONNECTOR_TYPES.has(conn.type.toLowerCase()) &&
                              form.ports.length > 0
                            ) {
                              const hasIncompatiblePorts = form.ports.some(
                                (p) =>
                                  !p.auth ||
                                  p.auth.mode === 'none' ||
                                  p.auth.mode !== 'entraId' ||
                                  p.auth.emails.length !== 1
                              );
                              if (hasIncompatiblePorts) {
                                alert(
                                  'Cannot add a personal connector because existing ports are not locked to your Entra ID email. Please remove incompatible ports first.'
                                );
                                return;
                              }
                            }
                            // Add to selection if not already selected
                            if (!form.selectedConnections.includes(value)) {
                              setForm((prev) => ({
                                ...prev,
                                selectedConnections: [...prev.selectedConnections, value],
                              }));
                            }
                          }
                        }}
                        placeholder={
                          connectionsLoading
                            ? 'Loading connections...'
                            : connections.length === 0
                              ? 'No connections available'
                              : 'Select a connection to add'
                        }
                        disabled={connectionsLoading || connections.length === 0}
                      />
                      {form.selectedConnections.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {form.selectedConnections.map((connId) => {
                            const conn = connections.find((c) => c.id === connId);
                            return (
                              <span
                                key={connId}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md text-sm"
                              >
                                {conn ? `${conn.name} (${conn.type})` : connId}
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((prev) => ({
                                      ...prev,
                                      selectedConnections: prev.selectedConnections.filter(
                                        (id) => id !== connId
                                      ),
                                    }))
                                  }
                                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Select connections to make available in the sandbox
                      </p>
                    </div>
                  </div>
                </CardBody>
              </div>
            </div>
          </Card>

          {/* Ports - Collapsible */}
          <Card className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setPortsExpanded(!portsExpanded)}
              className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${portsExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                  Ports
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                  Configure ports to expose on the sandbox (optional)
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${portsExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${portsExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <CardBody>
                  <PortsEditor
                    value={form.ports}
                    onChange={(ports) => setForm((prev) => ({ ...prev, ports }))}
                    helperText="Add ports to expose when the sandbox is created. You can also add ports later from the sandbox details page."
                    requireAuth={hasPersonalConnectors}
                    userEmail={user?.email}
                  />
                </CardBody>
              </div>
            </div>
          </Card>

          {/* Volumes - Collapsible (hidden in pod mode since volumes are per-container) */}
          {form.sourceType !== 'pod' && (
            <Card className="lg:col-span-2">
              <button
                type="button"
                onClick={() => setVolumesExpanded(!volumesExpanded)}
                className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${volumesExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                    Volumes
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                    {form.sourceType === 'snapshot'
                      ? 'Volumes are inherited from the snapshot and cannot be changed'
                      : 'Mount volumes into the sandbox (optional)'}
                  </p>
                </div>
                {form.sourceType !== 'snapshot' && (
                  <ChevronDown
                    className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${volumesExpanded ? 'rotate-180' : ''}`}
                  />
                )}
              </button>
              {form.sourceType === 'snapshot' ? (
                <CardBody>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      When creating a sandbox from a snapshot, volumes cannot be specified. The
                      snapshot&apos;s existing volume configuration will be used automatically.
                    </p>
                  </div>
                </CardBody>
              ) : (
                <div
                  className={`grid transition-[grid-template-rows] duration-200 ease-out ${volumesExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                >
                  <div className="overflow-hidden">
                    <CardBody>
                      <div className="space-y-4">
                        {/* Committed volume mounts */}
                        {form.volumeMounts.length > 0 && (
                          <div className="space-y-2">
                            {form.volumeMounts.map((mount, index) => {
                              const volInfo = volumesData?.find(
                                (v) => v.volumeName === mount.volumeName
                              );
                              const isDataDisk = volInfo?.type === 'DataDisk';
                              return (
                                <div
                                  key={index}
                                  className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                      {mount.volumeName}
                                    </span>
                                    <span
                                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${isDataDisk ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}`}
                                    >
                                      {isDataDisk ? 'dataDisk' : 'azureBlob'}
                                    </span>
                                    <span className="text-gray-400 dark:text-gray-500">→</span>
                                    <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#222] px-1.5 py-0.5 rounded truncate">
                                      {mount.mountpoint}
                                    </code>
                                    {mount.readOnly ? (
                                      <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded font-medium">
                                        read-only
                                      </span>
                                    ) : (
                                      <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded font-medium">
                                        read-write
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = form.volumeMounts.filter(
                                        (_, i) => i !== index
                                      );
                                      setForm((prev) => ({ ...prev, volumeMounts: updated }));
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                    title="Remove volume mount"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* DataDisk lifecycle constraint notice */}
                        {hasNonPodDataDiskVolumes && (
                          <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <Info className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              Suspend mode has been set to <strong>Disk</strong> because data disk
                              volumes do not support memory snapshots. You can change this in
                              Lifecycle Policy if you remove the data disk volume.
                            </p>
                          </div>
                        )}

                        {/* Add new volume mount form */}
                        {(() => {
                          const availableVolumes = volumesData?.filter(
                            (v) =>
                              !form.volumeMounts.some((m) => m.volumeName === v.volumeName) &&
                              // Exclude DataDisk volumes that are already attached to another sandbox
                              !(v.type === 'DataDisk' && v.isAttached)
                          );
                          // Filter by currently selected kind
                          const filteredVolumes = availableVolumes?.filter((v) =>
                            draftVolumeMountKind === 'DataDisk'
                              ? v.type === 'DataDisk'
                              : v.type !== 'DataDisk'
                          );
                          const hasAvailable = availableVolumes && availableVolumes.length > 0;

                          if (!hasAvailable && !volumesLoading) {
                            if (!volumesData || volumesData.length === 0) {
                              return (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  No volumes available.{' '}
                                  <Link
                                    to={
                                      routeGroupId
                                        ? `/sandbox-groups/${encodeURIComponent(routeGroupId)}/volumes/new`
                                        : '/volumes/new'
                                    }
                                    className="text-primary-600 dark:text-primary-400 hover:underline"
                                  >
                                    Create a volume
                                  </Link>{' '}
                                  first.
                                </p>
                              );
                            }
                            if (form.volumeMounts.length > 0) {
                              return (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  All available volumes have been added.
                                </p>
                              );
                            }
                          }

                          return (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDraftVolumeMountKind('AzureBlob');
                                    setDraftVolumeMount((prev) => ({ ...prev, volumeName: '' }));
                                  }}
                                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                                    draftVolumeMountKind === 'AzureBlob'
                                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                      : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                                  }`}
                                >
                                  Azure Blob
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDraftVolumeMountKind('DataDisk');
                                    setDraftVolumeMount((prev) => ({ ...prev, volumeName: '' }));
                                  }}
                                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                                    draftVolumeMountKind === 'DataDisk'
                                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                      : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                                  }`}
                                >
                                  Data Disk
                                </button>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="flex-1">
                                  <Select
                                    label="Volume"
                                    options={
                                      filteredVolumes?.map((v) => ({
                                        value: v.volumeName,
                                        label:
                                          draftVolumeMountKind === 'DataDisk' && v.size
                                            ? `${v.volumeName} (${v.size})`
                                            : v.volumeName,
                                      })) || []
                                    }
                                    value={draftVolumeMount.volumeName}
                                    onChange={(e) =>
                                      setDraftVolumeMount((prev) => ({
                                        ...prev,
                                        volumeName: e.target.value,
                                      }))
                                    }
                                    placeholder={
                                      volumesLoading
                                        ? 'Loading volumes...'
                                        : draftVolumeMountKind === 'DataDisk'
                                          ? 'Select a data disk volume'
                                          : 'Select an Azure Blob volume'
                                    }
                                    disabled={volumesLoading}
                                  />
                                </div>
                                <div className="flex-1">
                                  <Input
                                    label="Mount Path"
                                    placeholder="/mnt/data"
                                    value={draftVolumeMount.mountpoint}
                                    onChange={(e) => {
                                      setDraftVolumeMount((prev) => ({
                                        ...prev,
                                        mountpoint: e.target.value,
                                      }));
                                      if (mountPathError) setMountPathError(undefined);
                                    }}
                                    onBlur={() => {
                                      let cleaned = draftVolumeMount.mountpoint.trim();
                                      if (cleaned && !cleaned.startsWith('/')) {
                                        cleaned = '/' + cleaned;
                                      }
                                      // Strip trailing slashes (but keep a lone /)
                                      while (cleaned.length > 1 && cleaned.endsWith('/')) {
                                        cleaned = cleaned.slice(0, -1);
                                      }
                                      if (cleaned !== draftVolumeMount.mountpoint) {
                                        setDraftVolumeMount((prev) => ({
                                          ...prev,
                                          mountpoint: cleaned,
                                        }));
                                      }
                                    }}
                                    error={mountPathError}
                                  />
                                </div>
                                <div className="flex items-center gap-2 mt-7">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setDraftVolumeMount((prev) => ({
                                        ...prev,
                                        readOnly: !prev.readOnly,
                                      }))
                                    }
                                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                                      draftVolumeMount.readOnly
                                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                        : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500'
                                    }`}
                                    title={
                                      draftVolumeMount.readOnly
                                        ? 'Click to make read-write'
                                        : 'Click to make read-only'
                                    }
                                  >
                                    {draftVolumeMount.readOnly ? (
                                      <CheckCircle className="h-3.5 w-3.5" />
                                    ) : (
                                      <Circle className="h-3.5 w-3.5" />
                                    )}
                                    Read-only
                                  </button>
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    leftIcon={<Plus className="h-3.5 w-3.5" />}
                                    disabled={
                                      !draftVolumeMount.volumeName ||
                                      !draftVolumeMount.mountpoint.trim()
                                    }
                                    onClick={() => {
                                      let mountpoint = draftVolumeMount.mountpoint
                                        .trim()
                                        .startsWith('/')
                                        ? draftVolumeMount.mountpoint.trim()
                                        : '/' + draftVolumeMount.mountpoint.trim();
                                      // Strip trailing slashes
                                      while (mountpoint.length > 1 && mountpoint.endsWith('/')) {
                                        mountpoint = mountpoint.slice(0, -1);
                                      }

                                      // Validate linux path
                                      if (mountpoint === '/') {
                                        setMountPathError('Cannot mount at root /');
                                        return;
                                      }
                                      if (/\0/.test(mountpoint)) {
                                        setMountPathError('Path contains invalid characters');
                                        return;
                                      }
                                      if (/\/\//.test(mountpoint)) {
                                        setMountPathError('Path contains consecutive slashes');
                                        return;
                                      }
                                      if (/[\s]/.test(mountpoint)) {
                                        setMountPathError('Path should not contain whitespace');
                                        return;
                                      }

                                      setMountPathError(undefined);
                                      setForm((prev) => ({
                                        ...prev,
                                        volumeMounts: [
                                          ...prev.volumeMounts,
                                          { ...draftVolumeMount, mountpoint },
                                        ],
                                      }));
                                      setDraftVolumeMount({
                                        volumeName: '',
                                        mountpoint: '',
                                        readOnly: false,
                                      });
                                    }}
                                  >
                                    Add
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </CardBody>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Lifecycle Policy - Collapsible */}
          <Card className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setLifecyclePolicyExpanded(!lifecyclePolicyExpanded)}
              className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${lifecyclePolicyExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                  Lifecycle Policy
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                  Configure auto-suspend and other lifecycle behaviors
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${lifecyclePolicyExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${lifecyclePolicyExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <CardBody>
                  {/* Auto-Suspend Policy */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Auto-Suspend Policy
                    </label>
                    <div className="space-y-3">
                      <Checkbox
                        label="Enable auto-suspend"
                        checked={
                          form.autoSuspendEnabled &&
                          !(hasPodDataDiskVolumes && form.sourceType === 'pod')
                        }
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, autoSuspendEnabled: e.target.checked }))
                        }
                        helperText="Automatically suspend the sandbox after a period of inactivity"
                        disabled={hasPodDataDiskVolumes && form.sourceType === 'pod'}
                      />
                      {hasPodDataDiskVolumes && form.sourceType === 'pod' && (
                        <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            Auto-suspend is disabled because data disk volumes are attached. Suspend
                            is not yet supported for pod sandboxes with data disk volumes.
                          </p>
                        </div>
                      )}
                      {form.autoSuspendEnabled &&
                        !(hasPodDataDiskVolumes && form.sourceType === 'pod') && (
                          <div className="ml-7 space-y-4">
                            <Input
                              label="Idle timeout"
                              placeholder="5m"
                              value={form.autoSuspendInterval}
                              onChange={(e) => handleChange('autoSuspendInterval', e.target.value)}
                              helperText={
                                parseDuration(form.autoSuspendInterval) !== null
                                  ? `= ${formatDurationLong(parseDuration(form.autoSuspendInterval)!)}. Use a duration like 1s, 5m, 2h, or 1d.`
                                  : 'Use a duration like 1s, 5m, 2h, or 1d.'
                              }
                            />
                            {parseDuration(form.autoSuspendInterval) === null &&
                              form.autoSuspendInterval.trim() !== '' && (
                                <p className="text-xs text-danger-600 dark:text-danger-400">
                                  Invalid duration format. Use values like 1s, 5m, 2h, 1d.
                                </p>
                              )}
                            {parseDuration(form.autoSuspendInterval) !== null &&
                              parseDuration(form.autoSuspendInterval)! <= 0 && (
                                <p className="text-xs text-danger-600 dark:text-danger-400">
                                  Interval must be greater than 0.
                                </p>
                              )}
                            <Select
                              label="Suspend Mode"
                              options={[
                                {
                                  value: 'Memory',
                                  label: 'Memory (Full snapshot with memory state)',
                                },
                                {
                                  value: 'Disk',
                                  label: 'Disk (Preserve disk only, VM restarts fresh)',
                                },
                              ]}
                              value={hasNonPodDataDiskVolumes ? 'Disk' : form.autoSuspendMode}
                              onChange={(e) =>
                                handleChange(
                                  'autoSuspendMode',
                                  e.target.value as SandboxSuspendMode
                                )
                              }
                              disabled={hasNonPodDataDiskVolumes}
                            />
                            {hasNonPodDataDiskVolumes && (
                              <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                  Suspend mode is set to <strong>Disk</strong> because data disk
                                  volumes are attached. Memory snapshots are not supported with data
                                  disk volumes.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Auto-Delete Policy */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Auto-Delete Policy
                    </label>
                    <div className="space-y-3">
                      <Checkbox
                        label="Enable auto-delete"
                        checked={form.autoDeleteEnabled}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, autoDeleteEnabled: e.target.checked }))
                        }
                        helperText="Automatically delete the sandbox after a period of being suspended"
                      />
                      {form.autoDeleteEnabled && (
                        <div className="ml-7 space-y-4">
                          <Input
                            label="Delete after"
                            placeholder="1d"
                            value={form.autoDeleteIntervalSeconds}
                            onChange={(e) =>
                              handleChange('autoDeleteIntervalSeconds', e.target.value)
                            }
                            helperText={
                              parseDuration(form.autoDeleteIntervalSeconds) !== null
                                ? `= ${formatDurationLong(parseDuration(form.autoDeleteIntervalSeconds)!)}. Use a duration like 30m, 2h, 1d, or 7d.`
                                : 'Use a duration like 30m, 2h, 1d, or 7d.'
                            }
                          />
                          {parseDuration(form.autoDeleteIntervalSeconds) === null &&
                            form.autoDeleteIntervalSeconds.trim() !== '' && (
                              <p className="text-xs text-danger-600 dark:text-danger-400">
                                Invalid duration format. Use values like 30m, 2h, 1d, 7d.
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </div>
            </div>
          </Card>

          {/* Network Egress Policy - Collapsible */}
          <Card className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setEgressPolicyExpanded(!egressPolicyExpanded)}
              className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${egressPolicyExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                  Network Egress Policy
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                  Configure outbound network access rules (optional)
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${egressPolicyExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${egressPolicyExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <CardBody>
                  <EgressPolicyEditor
                    defaultAction={form.egressDefaultAction}
                    onDefaultActionChange={(action) =>
                      setForm((prev) => ({ ...prev, egressDefaultAction: action }))
                    }
                    hostRules={form.egressHostRules}
                    onHostRulesChange={(rules) =>
                      setForm((prev) => ({ ...prev, egressHostRules: rules }))
                    }
                    rules={form.egressRules}
                    onRulesChange={(rules) => setForm((prev) => ({ ...prev, egressRules: rules }))}
                    trafficInspection={form.trafficInspection}
                    onTrafficInspectionChange={(mode) =>
                      setForm((prev) => ({ ...prev, trafficInspection: mode }))
                    }
                    availableSecrets={secretsData}
                    sandboxGroupId={routeGroupId}
                  />
                </CardBody>
              </div>
            </div>
          </Card>

          {/* Telemetry - Collapsible */}
          <Card className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setTelemetryExpanded(!telemetryExpanded)}
              className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${telemetryExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                  Telemetry
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                  Configure telemetry endpoints to export logs, metrics, and traces (optional)
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${telemetryExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${telemetryExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <CardBody>
                  <div className="space-y-4">
                    {/* Committed endpoints (all kinds) */}
                    {(form.otlpEndpoints.length > 0 ||
                      form.logAnalyticsEndpoints.length > 0 ||
                      form.logAnalyticsLegacyEndpoints.length > 0) && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Configured Endpoints
                        </label>
                        {form.otlpEndpoints.map((ep, index) => (
                          <div
                            key={`otlp-${index}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-medium">
                                OTLP
                              </span>
                              <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#222] px-1.5 py-0.5 rounded truncate max-w-xs">
                                {ep.endpoint}
                              </code>
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                                {ep.protocol}
                              </span>
                              {ep.data.map((d) => (
                                <span
                                  key={d}
                                  className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded"
                                >
                                  {d}
                                </span>
                              ))}
                              {ep.auth && (
                                <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-medium">
                                  Auth: {ep.auth.headerName}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = form.otlpEndpoints.filter((_, i) => i !== index);
                                setForm((prev) => ({ ...prev, otlpEndpoints: updated }));
                              }}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              title="Remove endpoint"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        {form.logAnalyticsEndpoints.map((ep, index) => (
                          <div
                            key={`la-${index}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded font-medium">
                                Log Analytics
                              </span>
                              <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#222] px-1.5 py-0.5 rounded truncate max-w-xs">
                                {ep.dceEndpoint}
                              </code>
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                                {ep.tableName}
                              </span>
                              {ep.data.map((d) => (
                                <span
                                  key={d}
                                  className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded"
                                >
                                  {d}
                                </span>
                              ))}
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-medium">
                                Managed Identity
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = form.logAnalyticsEndpoints.filter(
                                  (_, i) => i !== index
                                );
                                setForm((prev) => ({
                                  ...prev,
                                  logAnalyticsEndpoints: updated,
                                }));
                              }}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              title="Remove endpoint"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        {form.logAnalyticsLegacyEndpoints.map((ep, index) => (
                          <div
                            key={`legacy-${index}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded font-medium">
                                Log Analytics Legacy
                              </span>
                              <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#222] px-1.5 py-0.5 rounded truncate max-w-xs">
                                {ep.workspaceId}
                              </code>
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                                {ep.tableName}
                              </span>
                              {ep.data.map((d) => (
                                <span
                                  key={d}
                                  className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded"
                                >
                                  {d}
                                </span>
                              ))}
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-medium">
                                Secret: {ep.auth.secretId}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = form.logAnalyticsLegacyEndpoints.filter(
                                  (_, i) => i !== index
                                );
                                setForm((prev) => ({
                                  ...prev,
                                  logAnalyticsLegacyEndpoints: updated,
                                }));
                              }}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              title="Remove endpoint"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Endpoint kind selector */}
                    <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Add Telemetry Endpoint
                      </label>
                      <div className="flex gap-2">
                        {(
                          [
                            { value: 'OTLP', label: 'OTLP' },
                            { value: 'LogAnalytics', label: 'Log Analytics' },
                            { value: 'LogAnalyticsLegacy', label: 'Log Analytics Legacy' },
                          ] as { value: TelemetryEndpointKind; label: string }[]
                        ).map((kind) => (
                          <button
                            key={kind.value}
                            type="button"
                            onClick={() => setDraftEndpointKind(kind.value)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-colors cursor-pointer ${
                              draftEndpointKind === kind.value
                                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                                : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                          >
                            {kind.label}
                          </button>
                        ))}
                      </div>

                      {/* OTLP endpoint form */}
                      {draftEndpointKind === 'OTLP' && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input
                              label="Endpoint URL"
                              placeholder="https://otlp.example.com:4318"
                              value={draftOtlpEndpoint.endpoint}
                              onChange={(e) =>
                                setDraftOtlpEndpoint((prev) => ({
                                  ...prev,
                                  endpoint: e.target.value,
                                }))
                              }
                              helperText="OTLP receiver URL"
                            />
                            <Select
                              label="Protocol"
                              options={[
                                { value: 'Http', label: 'HTTP' },
                                { value: 'Grpc', label: 'gRPC' },
                              ]}
                              value={draftOtlpEndpoint.protocol}
                              onChange={(e) =>
                                setDraftOtlpEndpoint((prev) => ({
                                  ...prev,
                                  protocol: e.target.value as TelemetryProtocol,
                                }))
                              }
                            />
                            <TelemetryDataPicker
                              selected={draftOtlpEndpoint.data}
                              onChange={(data) =>
                                setDraftOtlpEndpoint((prev) => ({ ...prev, data }))
                              }
                            />
                          </div>

                          {/* Auth configuration (optional) */}
                          <div className="pt-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Authentication (Optional)
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <Input
                                label="Header Name"
                                placeholder="x-api-key"
                                value={draftOtlpEndpoint.auth?.headerName ?? ''}
                                onChange={(e) =>
                                  setDraftOtlpEndpoint((prev) => ({
                                    ...prev,
                                    auth: e.target.value
                                      ? {
                                          headerName: e.target.value,
                                          secretId: prev.auth?.secretId ?? '',
                                          secretKey: prev.auth?.secretKey ?? '',
                                        }
                                      : undefined,
                                  }))
                                }
                                helperText="HTTP header to inject"
                              />
                              <Select
                                label="Secret ID"
                                options={[
                                  { value: '', label: 'Select a secret...' },
                                  ...(secretsData ?? []).map((s) => ({
                                    value: s.id,
                                    label: s.id,
                                  })),
                                ]}
                                value={draftOtlpEndpoint.auth?.secretId ?? ''}
                                onChange={(e) =>
                                  setDraftOtlpEndpoint((prev) => ({
                                    ...prev,
                                    auth: prev.auth?.headerName
                                      ? {
                                          ...prev.auth,
                                          secretId: e.target.value,
                                        }
                                      : undefined,
                                  }))
                                }
                                disabled={!draftOtlpEndpoint.auth?.headerName}
                              />
                              <Input
                                label="Secret Key"
                                placeholder="api-key"
                                value={draftOtlpEndpoint.auth?.secretKey ?? ''}
                                onChange={(e) =>
                                  setDraftOtlpEndpoint((prev) => ({
                                    ...prev,
                                    auth: prev.auth?.headerName
                                      ? {
                                          ...prev.auth,
                                          secretKey: e.target.value,
                                        }
                                      : undefined,
                                  }))
                                }
                                disabled={!draftOtlpEndpoint.auth?.headerName}
                                helperText="Key within the secret"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              leftIcon={<Plus className="h-3.5 w-3.5" />}
                              disabled={
                                !draftOtlpEndpoint.endpoint.trim() ||
                                draftOtlpEndpoint.data.length === 0
                              }
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  otlpEndpoints: [...prev.otlpEndpoints, { ...draftOtlpEndpoint }],
                                }));
                                setDraftOtlpEndpoint({
                                  endpoint: '',
                                  protocol: 'Http',
                                  data: [],
                                  auth: undefined,
                                });
                              }}
                            >
                              Add Endpoint
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Log Analytics (Managed Identity) endpoint form */}
                      {draftEndpointKind === 'LogAnalytics' && (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Uses the Logs Ingestion API with Managed Identity authentication (no
                            secrets required).
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              label="DCE Endpoint"
                              placeholder="https://dce-xxx.eastus-1.ingest.monitor.azure.com"
                              value={draftLogAnalyticsEndpoint.dceEndpoint}
                              onChange={(e) =>
                                setDraftLogAnalyticsEndpoint((prev) => ({
                                  ...prev,
                                  dceEndpoint: e.target.value,
                                }))
                              }
                              helperText="Data Collection Endpoint URL"
                            />
                            <Input
                              label="DCR Immutable ID"
                              placeholder="dcr-a1b2c3d4e5f6..."
                              value={draftLogAnalyticsEndpoint.dcrImmutableId}
                              onChange={(e) =>
                                setDraftLogAnalyticsEndpoint((prev) => ({
                                  ...prev,
                                  dcrImmutableId: e.target.value,
                                }))
                              }
                              helperText="Data Collection Rule immutable ID"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              label="Table Name"
                              placeholder="Custom-SandboxLogs_CL"
                              value={draftLogAnalyticsEndpoint.tableName}
                              onChange={(e) =>
                                setDraftLogAnalyticsEndpoint((prev) => ({
                                  ...prev,
                                  tableName: e.target.value,
                                }))
                              }
                              helperText="Target stream/table name in the DCR"
                            />
                            <TelemetryDataPicker
                              selected={draftLogAnalyticsEndpoint.data}
                              availableTypes={['ContainerStdoutStderr', 'ContainerOtel']}
                              onChange={(data) =>
                                setDraftLogAnalyticsEndpoint((prev) => ({ ...prev, data }))
                              }
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              leftIcon={<Plus className="h-3.5 w-3.5" />}
                              disabled={
                                !draftLogAnalyticsEndpoint.dceEndpoint.trim() ||
                                !draftLogAnalyticsEndpoint.dcrImmutableId.trim() ||
                                !draftLogAnalyticsEndpoint.tableName.trim() ||
                                draftLogAnalyticsEndpoint.data.length === 0
                              }
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  logAnalyticsEndpoints: [
                                    ...prev.logAnalyticsEndpoints,
                                    { ...draftLogAnalyticsEndpoint },
                                  ],
                                }));
                                setDraftLogAnalyticsEndpoint({
                                  dceEndpoint: '',
                                  dcrImmutableId: '',
                                  tableName: '',
                                  data: [],
                                });
                              }}
                            >
                              Add Endpoint
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Log Analytics Legacy endpoint form */}
                      {draftEndpointKind === 'LogAnalyticsLegacy' && (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Uses the Log Analytics Data Collector API with shared key
                            authentication.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input
                              label="Workspace ID"
                              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                              value={draftLegacyEndpoint.workspaceId}
                              onChange={(e) =>
                                setDraftLegacyEndpoint((prev) => ({
                                  ...prev,
                                  workspaceId: e.target.value,
                                }))
                              }
                              helperText="Log Analytics workspace ID"
                            />
                            <Input
                              label="Table Name"
                              placeholder="ContainerAppConsoleLogs"
                              value={draftLegacyEndpoint.tableName}
                              onChange={(e) =>
                                setDraftLegacyEndpoint((prev) => ({
                                  ...prev,
                                  tableName: e.target.value,
                                }))
                              }
                              helperText="Custom log table name"
                            />
                            <TelemetryDataPicker
                              selected={draftLegacyEndpoint.data}
                              availableTypes={['ContainerStdoutStderr']}
                              onChange={(data) =>
                                setDraftLegacyEndpoint((prev) => ({ ...prev, data }))
                              }
                            />
                          </div>

                          {/* Shared key auth (required) */}
                          <div className="pt-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Shared Key Authentication
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <Select
                                label="Secret ID"
                                options={[
                                  { value: '', label: 'Select a secret...' },
                                  ...(secretsData ?? []).map((s) => ({
                                    value: s.id,
                                    label: s.id,
                                  })),
                                ]}
                                value={draftLegacyEndpoint.auth.secretId}
                                onChange={(e) =>
                                  setDraftLegacyEndpoint((prev) => ({
                                    ...prev,
                                    auth: { ...prev.auth, secretId: e.target.value },
                                  }))
                                }
                              />
                              <Input
                                label="Secret Key"
                                placeholder="primary-key"
                                value={draftLegacyEndpoint.auth.secretKey}
                                onChange={(e) =>
                                  setDraftLegacyEndpoint((prev) => ({
                                    ...prev,
                                    auth: { ...prev.auth, secretKey: e.target.value },
                                  }))
                                }
                                helperText="Key within the secret containing the workspace shared key"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              leftIcon={<Plus className="h-3.5 w-3.5" />}
                              disabled={
                                !draftLegacyEndpoint.workspaceId.trim() ||
                                !draftLegacyEndpoint.tableName.trim() ||
                                draftLegacyEndpoint.data.length === 0 ||
                                !draftLegacyEndpoint.auth.secretId ||
                                !draftLegacyEndpoint.auth.secretKey.trim()
                              }
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  logAnalyticsLegacyEndpoints: [
                                    ...prev.logAnalyticsLegacyEndpoints,
                                    { ...draftLegacyEndpoint },
                                  ],
                                }));
                                setDraftLegacyEndpoint({
                                  workspaceId: '',
                                  tableName: '',
                                  data: ['ContainerStdoutStderr'],
                                  auth: { secretId: '', secretKey: '' },
                                });
                              }}
                            >
                              Add Endpoint
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </div>
            </div>
          </Card>

          {/* Content Packages - Collapsible (hidden in pod mode since content packages are per-container) */}
          {form.sourceType !== 'pod' && (
            <Card className="lg:col-span-2">
              <button
                type="button"
                onClick={() => setContentPackagesExpanded(!contentPackagesExpanded)}
                className={`w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${contentPackagesExpanded ? 'border-b border-gray-100 dark:border-[#2a2a2a]' : ''}`}
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-left">
                    Content Packages
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-left">
                    Download content packages into the sandbox at creation time (optional)
                  </p>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${contentPackagesExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out ${contentPackagesExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              >
                <div className="overflow-hidden">
                  <CardBody>
                    <ContentPackageDownloadsEditor
                      value={form.contentPackageDownloads}
                      onChange={(downloads) =>
                        setForm((prev) => ({ ...prev, contentPackageDownloads: downloads }))
                      }
                      contentPackages={contentPackagesData}
                      isLoading={contentPackagesLoading}
                    />
                  </CardBody>
                </div>
              </div>
            </Card>
          )}

          {/* Resources */}
          <Card className="lg:col-span-2">
            <CardHeader
              title="Resources"
              description={
                form.sourceType === 'snapshot'
                  ? 'Resource configuration is defined by the snapshot and cannot be modified'
                  : 'Configure the resources for the sandbox'
              }
            />
            <CardBody>
              {form.sourceType === 'snapshot' ? (
                <div>
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      When creating a sandbox from a snapshot, the resource configuration (CPU,
                      memory, and disk) is inherited from the snapshot and cannot be changed.
                    </p>
                  </div>
                  {selectedSnapshot?.resources ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          CPU
                        </label>
                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300">
                          {selectedSnapshot.resources.cpu}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Memory
                        </label>
                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300">
                          {selectedSnapshot.resources.memory}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Disk
                        </label>
                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300">
                          {selectedSnapshot.resources.disk || 'N/A'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {form.snapshotId
                        ? 'Resource information is not available for this snapshot.'
                        : 'Select a snapshot to view its resource configuration.'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Resource Tier"
                    options={[
                      {
                        value: '250m|512Mi|20Gi',
                        label: 'XS — 0.25 cores / 0.5 GB RAM / 20 GB Disk',
                      },
                      { value: '500m|1Gi|20Gi', label: 'S — 0.5 cores / 1 GB RAM / 20 GB Disk' },
                      { value: '1000m|2Gi|20Gi', label: 'M — 1 core / 2 GB RAM / 20 GB Disk' },
                      { value: '2000m|4Gi|40Gi', label: 'L — 2 cores / 4 GB RAM / 40 GB Disk' },
                    ]}
                    value={`${form.cpu}|${form.memory}|${form.disk}`}
                    onChange={(e) => {
                      const [cpu, memory, disk] = e.target.value.split('|');
                      setForm((prev) => ({ ...prev, cpu, memory, disk }));
                    }}
                    error={errors.cpu}
                    required
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Selected Resources
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-600 dark:text-gray-300">
                      CPU: {form.cpu} · Memory: {form.memory} · Disk: {form.disk}
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to="/sandboxes">
                  <Button variant="secondary" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<Save className="h-4 w-4" />}
                  isLoading={createMutation.isPending}
                  disabled={
                    form.sourceType === 'diskImage'
                      ? privateDiskImageOptions.length === 0
                      : form.sourceType === 'publicDiskImage'
                        ? publicDiskImageOptions.length === 0
                        : form.sourceType === 'pod'
                          ? form.podContainers.length === 0
                          : availableSnapshots.length === 0
                  }
                >
                  Create Sandbox
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
